import { action, computed, makeAutoObservable, observable, ObservableSet, runInAction, transaction } from "mobx";
import React from "react";
import { useContext } from "react";
import {
  WallType, SymbolType, isSymbolType, SingleLineType, isSingleLineType,
  isWallType, Ruler3DType, RulerLineType, isRulerLineType, isRuler3DType, WallConnectionType,
  DoorType, WindowType, TextType, SquareSymbolType, CircleSymbolType,
  TriangleSymbolType, isDoorType, isSlidingDoorType, isWindowType,
  isSquareSymbolType, isCircleSymbolType, isTriangleSymbolType, isPolygonSymbolType,
  isCircleStairsType,
  isRectStairsType,
  isTurnStairsType, isSvgType, isSmartTableType, isBookShelfType, isDoubleDoorType,
  WallEdgeConnection,
  FloorplannerSelectionType,
  AreaFillSymbolType,
  isAreaFillSymbolType,
} from '../types/floorplannerTypes';
import type { FloorplannerStore } from "./floorplannerStore";
import type { RenderStoreType } from "./renderStore";
import { toOptionalVector2, toVector2, toVector2Array } from "../utils/vector2Serialization";
import { getTurnStairsPlanBounds } from "../features/floorplan3d/symbols/primitives/stairs/stairGeometry";
import {
  LengthUnit,
  parseLengthUnit,
} from "../utils/conversions";
import { isMobileUi } from "../utils/mobileUi";

// Lazy accessor to avoid circular dependency with floorplannerStore
// floorplannerStore imports editorStore, so we can't import it directly at module load time
let _floorplannerStore: FloorplannerStore | null = null;
const getFloorplannerStoreInstance = (): FloorplannerStore => {
  if (!_floorplannerStore) {
    const globalStore = (globalThis as typeof globalThis & { __boniFloorplannerStore?: FloorplannerStore }).__boniFloorplannerStore;
    if (globalStore) {
      _floorplannerStore = globalStore;
    } else {
      throw new Error('FloorplannerStore not yet initialized');
    }
  }
  return _floorplannerStore;
};

/**
 * Read the collab lock without requiring the floorplanner store to exist yet.
 * These guards run from keyboard and pointer handlers that can fire before the
 * store is wired up, where throwing would break plain view interactions.
 */
const isLockedByOtherCollaborator = (): boolean => {
  try {
    return getFloorplannerStoreInstance().lockedByOther;
  } catch {
    return false;
  }
};
import { ApolloClient } from "@apollo/client";
import * as THREE from "three";
import { invalidate } from "@react-three/fiber";
import {
  updateWallClippings,
  updateWallConnections,
} from "../components/FloorPlan/updateWallConnections";
import { translateWallOrLineByWorldDelta } from "../components/FloorPlan/translateWallOrLineByWorldDelta";
import { reaction } from "mobx";
import { undoRedoManager } from "./undoRedoManager";
import { evaluateWallAt, getWallArcLength } from "../utils/arcUtils";
import { isFloorplannerDebugLoggingEnabled } from "../utils/debugLogging";
import { resolveSmartTableLayout } from "../features/floorplan3d/symbols/dining/smartTableLayout";
import { resolveBookShelfLayout } from "../features/floorplan3d/symbols/livingRoom/bookShelfLayout";

// Add these type definitions before the EditorStore class
// Lazy accessor to avoid circular dependency with renderStore
let cachedRenderStore: RenderStoreType | null = null;
let renderStoreLookupWarned = false;

/**
 * Returns the globally cached renderStore instance, logging a warning the first
 * time it is requested before renderStore has been initialized.
 */
const getRenderStore = (): RenderStoreType | null => {
  if (!cachedRenderStore) {
    const globalRenderStore = (globalThis as typeof globalThis & { __boniRenderStore?: RenderStoreType }).__boniRenderStore;
    if (globalRenderStore) {
      cachedRenderStore = globalRenderStore;
    } else if (!renderStoreLookupWarned) {
      console.warn("renderStore is not yet available when requested from editorStore");
      renderStoreLookupWarned = true;
    }
  }
  return cachedRenderStore;
};

const isRenderStoreRendering = (): boolean => {
  const globalRenderStore = (globalThis as typeof globalThis & { __boniRenderStore?: RenderStoreType }).__boniRenderStore;
  const renderStore = cachedRenderStore ?? globalRenderStore;
  return renderStore?.rendering === true;
};

/** Keyboard nudge distances in floorplanner world units. */
const ARROW_KEY_NUDGE_FINE = 0.01;
const ARROW_KEY_NUDGE_COARSE = 0.05;

interface ClipboardWall extends WallType {
  originalId: string;
  selectedConnections?: WallConnectionType[];
  selectedEdgeConnections?: WallEdgeConnection[];
}

type ClipboardSymbol = (SymbolType | DoorType | WindowType | TextType |
  SquareSymbolType | CircleSymbolType | TriangleSymbolType) & {
    originalId: string;
    attachedToOriginal?: string;
  };

interface ClipboardSingleLine extends SingleLineType {
  originalId: string;
}

interface ClipboardRulerLine extends RulerLineType {
  originalId: string;
}

type ClipboardItem = ClipboardWall | ClipboardSymbol | ClipboardSingleLine | ClipboardRulerLine;

const normalizeEdgeConnectionVectors = (connection: WallEdgeConnection): WallEdgeConnection => ({
  ...connection,
  sourceEdgePoint: toVector2(connection.sourceEdgePoint),
  targetEdgePoint: toVector2(connection.targetEdgePoint),
});

type ResetZoomStats = {
  hasScene: boolean;
  hasValidBox: boolean;
  center?: { x: number; y: number; z: number };
  size?: { x: number; y: number; z: number };
  meshCount?: number;
  validBoxCount?: number;
  timestamp: number;
};

type ResetZoomOptions = {
  warnOnInvalidBounds?: boolean;
  /**
   * Prefer this box over a full scene mesh traverse. Used during load so camera
   * fit does not wait on expensive setFromObject over thousands of 2D meshes.
   */
  preferredBox?: THREE.Box3;
};

type ViewBounds2D = {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
};

export type FloorPlan3DPreset = "dollhouse" | "birdsEye" | "front" | "back" | "left" | "right";

export const MIN_INTERACTIVE_ZOOM_LEVEL = 0.2;
const MAX_INTERACTIVE_ZOOM_LEVEL = 14;
const ZOOM_STEP_FACTOR = 1.12;
const WHEEL_DELTA_PER_ZOOM_STEP = 100;
const MAX_WHEEL_ZOOM_STEPS = 4;
export const MEASUREMENT_ZOOM_SETTLE_DELAY_MS = 140;
const FLOORPLANNER_CANVAS_ID = "floorplanner-canvas";
const LEFT_INSERTION_MARGIN_PX = 180;
const RIGHT_INSERTION_MARGIN_PX = 250;
const PASTE_3D_OFFSET_METERS = 0.3;
/** localStorage key for mm/in display preference (UI only; not floorplan JSON). */
const DISPLAY_LENGTH_UNIT_STORAGE_KEY = "boni.displayLengthUnit";

const readPersistedDisplayLengthUnit = (): LengthUnit => {
  if (typeof window === "undefined") return "mm";
  try {
    return parseLengthUnit(localStorage.getItem(DISPLAY_LENGTH_UNIT_STORAGE_KEY)) ?? "mm";
  } catch {
    return "mm";
  }
};

class EditorStore {
  client: ApolloClient<any> | undefined;
  // Lazy getter to avoid circular dependency during module initialization
  get floorplannerStore(): FloorplannerStore {
    return getFloorplannerStoreInstance();
  }
  view3D = false;
  view3DPreset: FloorPlan3DPreset = "birdsEye";
  view3DPresetRequestId = 0;
  view3DZoomRequestId = 0;
  view3DZoomRequestSteps = 0;
  view3DResetZoomRequestId = 0;
  /**
   * 3D performance: skip catalog surface/furniture albedo textures and use
   * solid colors instead. Session-only; not persisted with the plan.
   */
  hide3DMaterialTextures = false;
  /**
   * 3D performance: disable shadow maps and sun shadow casting.
   * Session-only; not persisted with the plan.
   */
  hide3DShadows = false;
  /**
   * Force solid ceiling meshes even when the camera would normally hide them
   * (e.g. bird's-eye). Session-only; not persisted with the plan.
   */
  showCeilings = false;
  /**
   * Optional plan-space bounds for the next 3D reset-zoom. When set, the camera
   * frames these bounds instead of the full plan (e.g. newly added room).
   * Consumed (cleared) when the reset runs.
   */
  view3DResetZoomBounds: THREE.Box2 | null = null;
  floorplanAIRenderPanelOpen = false;
  render3DViewModalVisible = false;
  /** True while AI render generation (or post-render redirect) locks the UI. */
  render3DViewGenerating = false;
  /** Room-shape picker wizard (start flow or Measure panel "Add room"). */
  roomPickerVisible = false;
  selections = Array<WallType | SymbolType | SingleLineType | RulerLineType | Ruler3DType>();
  wallEditingOuterLength = undefined as string | undefined;
  wallEditingInnerLength = undefined as string | undefined;
  wallEditingLengthPanel = undefined as number | undefined;
  singleLineEditingLength = undefined as string | undefined;
  singleLineEditingLengthPanel = undefined as number | undefined;
  rulerLineEditingLength = undefined as string | undefined;
  areaEditingLength = undefined as string | undefined;
  rulerEditingLengthPanel = undefined as number | undefined;
  textEditing = undefined as string | undefined;
  // Track which wall has an attachment (door/window/void) being dragged on it
  // Used to hide the wall's measurement line during attachment drag
  attachmentDraggingOnWall = undefined as string | undefined;
  showMeasures = true;
  /**
   * Display preference for linear lengths (mm | in). Geometry stays in world metres.
   * Persisted separately from floorplan JSON.
   */
  displayLengthUnit: LengthUnit = readPersistedDisplayLengthUnit();
  /**
   * Session-only wall IDs temporarily hidden in the 3D view.
   * Never written to floorplan JSON / localStorage; cleared on plan load and page reload.
   */
  hiddenWallIds: ObservableSet<string> = observable.set<string>();
  textShow = true;
  showGrid = true;
  /** When true, the 2D canvas is shown in grayscale (CSS filter). Does not affect 3D or exports. */
  showBlackAndWhite = false;
  /**
   * Left toolbox collapsed. Independent of the property panel so each side
   * can be opened/closed on its own (especially on mobile).
   */
  minimized = isMobileUi();
  /**
   * Right property panel collapsed. Separate from `minimized` so closing
   * properties does not also hide the left menu.
   */
  propertyPanelMinimized = isMobileUi();
  @computed get canUndo() {
    return undoRedoManager.canUndo;
  }
  @computed get canRedo() {
    return undoRedoManager.canRedo;
  }

  // Debug visualization flags for edge-based connection system
  @observable debugEdgeConnections = false;
  @observable debugJointPolygons = false;
  @observable debugOutlineSegments = false;
  @observable debugWallLengths = false;
  @observable debugConnectionMetadata = false;
  @observable debugPerformance = false;
  @observable debugClippingPolygons = false;
  @observable debugZIndexLayers = false;
  @observable debugRenderingState = false;
  @observable debugImaginaryLines = false;
  @observable debugImaginaryIntersections = false;
  @observable debugFillCorners = false;
  @observable debugDisableWallGeometryCache = false;

  // Control visibility of DEBUG MODE panel (separate from debug flags)
  // Initialized based on URL parameter debug=1
  @observable debugPanelVisible = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

  @computed get isDebugModeActive(): boolean {
    return this.debugEdgeConnections ||
      this.debugJointPolygons ||
      this.debugOutlineSegments ||
      this.debugWallLengths ||
      this.debugConnectionMetadata ||
      this.debugPerformance ||
      this.debugClippingPolygons ||
      this.debugZIndexLayers ||
      this.debugRenderingState ||
      this.debugImaginaryLines ||
      this.debugImaginaryIntersections ||
      this.debugFillCorners;
  }

  clipboard: ClipboardItem[] = [];
  pasteOffset = new THREE.Vector2(0.2, 0.2);
  lastPastePosition: THREE.Vector2 | null = null;
  /**
   * True while paste object creation + pending wall geometry flush are in flight.
   * Used to show a non-blocking "Pasting..." overlay in Main.
   */
  isPasting = false;
  // Debug flag to visualize bounds during paste operation
  debugPasteVisualization = false;
  @observable.ref camera: THREE.PerspectiveCamera | THREE.OrthographicCamera | null = null;
  @observable.ref last2DCameraView = { x: 0, y: 0, z: 20, zoom: 1 };
  @observable zoomLevel = 1;
  @observable measurementZoomLevel = 1;
  @observable zoomSettling = false;
  private liveZoomLevel = 1;
  private zoomSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingSettledZoomLevel: number | null = null;
  wallConstructionMode = false;
  wallConstructionModeAddNewWall = false;
  wallConstructionModeUndoLastWallRequest = 0;
  lastWallConstructionUndoLastWallRequestAt = 0;
  /**
   * When set, WallConstructionPlane seeds the next wall start from a hover "+" click
   * instead of waiting for the first empty-canvas click.
   */
  wallConstructionSeedStart: {
    x: number;
    y: number;
    sourceWallId: string;
    attachEndpoint: "start" | "end" | "middle";
    projectedX: number;
    projectedY: number;
    outwardX: number;
    outwardY: number;
  } | null = null;
  /** Bumped on each hover-"+" start so construction can re-apply the seed after remounts. */
  wallConstructionSeedRequestId = 0;
  /** True while wall mode was entered from the hover "+" (shows bottom ESC hint). */
  wallConstructionFromHoverAdd = false;
  lineConstructionMode = false;
  lineConstructionModeAddNewLine = false;
  rulerConstructionMode = false;
  ruler3DConstructionMode = false;
  rulerConstructionModeAddNewLine = false;
  areaConstructionMode = false;
  areaFillConstructionMode = false;
  areaBleedFillMode = false;
  areaConstructionModeAddPoint = false;
  fillPropertiesOpenRequest = 0;
  /** Incremented to ask LeftPanel to open the Product Library (e.g. from Product List empty state). */
  productLibraryOpenRequest = 0;
  movementOffsetFine = ARROW_KEY_NUDGE_FINE;
  movementOffsetCorse = ARROW_KEY_NUDGE_COARSE;
  panning = false;
  previousPanningState: boolean | null = null;
  /** True while Space is held to temporarily enable panning. */
  spacePanningActive = false;
  spacePanPreviousState: boolean | null = null;
  groupRefsMap = new Map<string, THREE.Group | null>();
  isShiftPressed = false;
  isAltPressed = false; // Tracks ALT/OPTION key for center snapping mode
  wallDragging: string | null = null;
  lineDragging: string | null = null;
  rulerDragging: string | null = null;
  snappingTolerance = 0.1;
  @observable useEdgeConnections = false;
  whichEndToUpdate: "start" | "end" | undefined = undefined;
  // Download project
  downloadProjectVisible = false;
  downloadProjectPosition = { top: 0, left: 0 };
  // Add to project
  addToPresentationVisible = false;
  addToPresentationPosition = { top: 0, left: 0 };
  // Freemium paywall modal. `paywallReason` distinguishes which cap/feature was
  // hit so the modal can render the right copy and CTA. "download" and
  // "addToProject" gate paid-only export/integration flows independent of the
  // floorplan/shape count caps.
  @observable paywallVisible = false;
  @observable paywallReason:
    | "shapes"
    | "project"
    | "download"
    | "addToProject"
    | null = null;
  lastResetZoomStats: ResetZoomStats | null = null;

  // Preview mode for download/project - renders with consistent zoom regardless of editor state
  @observable previewMode = false;
  @observable previewZoomLevel = 1.0;

  // Symbol library state persistence
  symbolLibraryActiveTab = "FLOOR_PLAN";
  symbolLibraryScrollPositions = new Map<string, number>();

  constructor() {
    makeAutoObservable(this, {
      camera: observable.ref,
      last2DCameraView: observable.ref,
      zoomLevel: observable,
      measurementZoomLevel: observable,
      zoomSettling: observable,
      lastResetZoomStats: observable.ref,
      // Already an ObservableSet; do not re-wrap.
      hiddenWallIds: false,
    });
    document.addEventListener("keydown", this.handleKeyDown, true);
    document.addEventListener("beforeinput", this.handleBeforeInput, true);
    document.addEventListener("keyup", this.handleKeyUp, true);
    document.addEventListener("mousedown", this.handleMouseDown, true);
    document.addEventListener("mouseup", this.handleMouseUp, true);
    window.addEventListener("blur", this.handleWindowBlur);
    // Sync modifier keys on pointer move to fix stuck shift/alt state when keyup events are missed
    window.addEventListener("pointermove", this.handleGlobalPointerMove, { passive: true });

    // Add reaction to camera changes
    reaction(
      () => ({
        camera: this.camera,
        zoom: this.zoomLevel,
        position: this.camera ? [this.camera.position.x, this.camera.position.y, this.camera.position.z] : null
      }),
      () => {
        // Update view bounds visualization whenever camera changes
        if (this.debugPasteVisualization) {
          const renderStore = getRenderStore();
          if (renderStore?.scene) {
            // Clear existing debug lines
            renderStore.scene.children = renderStore.scene.children.filter(
              (child: any) => !(child instanceof THREE.Line && child.userData.isDebugLine)
            );

            // Redraw view bounds
            const visibleBounds = this.getVisibleBounds();
            if (visibleBounds) {
              const viewBoundsLine = this.createDebugLine(
                [
                  visibleBounds.xMin, visibleBounds.yMin, 0,
                  visibleBounds.xMax, visibleBounds.yMin, 0,
                  visibleBounds.xMax, visibleBounds.yMax, 0,
                  visibleBounds.xMin, visibleBounds.yMax, 0,
                  visibleBounds.xMin, visibleBounds.yMin, 0
                ],
                0x000080, // Dark blue color
                1.0, // Full opacity
                3 // Thicker line
              );
              viewBoundsLine.userData.isDebugLine = true;
              renderStore.scene.add(viewBoundsLine);
            }
          }
          invalidate();
        }
      }
    );
  }

  dispose() {
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("beforeinput", this.handleBeforeInput);
    document.removeEventListener("keyup", this.handleKeyUp);
    document.removeEventListener("mousedown", this.handleMouseDown);
    document.removeEventListener("mouseup", this.handleMouseUp);
    window.removeEventListener("blur", this.handleWindowBlur);
    window.removeEventListener("pointermove", this.handleGlobalPointerMove);
    this.clearZoomSettleTimer();
  }

  initialize(client: ApolloClient<any>) {
    this.client = client;
  }

  componentDidMount() {
    window.focus();
  }

  setCamera(camera: THREE.PerspectiveCamera | THREE.OrthographicCamera) {
    this.camera = camera;
    // Sync the zoom level when setting the camera
    if (camera) {
      this.setZoomLevel(camera.zoom);
    }
  }

  @action
  remember2DCameraView = (camera: THREE.PerspectiveCamera | THREE.OrthographicCamera) => {
    // Orthographic 2D ignores Z for scale, but 3D entry distance math still
    // uses the canonical height.
    this.last2DCameraView = {
      x: camera.position.x,
      y: camera.position.y,
      z: 20,
      zoom: camera.zoom,
    };
  };

  private clearZoomSettleTimer = () => {
    if (this.zoomSettleTimer) {
      clearTimeout(this.zoomSettleTimer);
      this.zoomSettleTimer = null;
    }
    this.pendingSettledZoomLevel = null;
    this.zoomSettling = false;
  };

  private commitSettledZoomLevel = (level: number) => {
    this.clearZoomSettleTimer();
    this.liveZoomLevel = level;
    this.zoomLevel = level;
    this.measurementZoomLevel = level;
  };

  private scheduleSettledZoomLevelUpdate = (level: number) => {
    this.pendingSettledZoomLevel = level;
    this.zoomSettling = true;

    if (this.zoomSettleTimer) {
      clearTimeout(this.zoomSettleTimer);
    }

    this.zoomSettleTimer = setTimeout(() => {
      const nextSettledZoomLevel = this.pendingSettledZoomLevel ?? this.liveZoomLevel;
      runInAction(() => {
        this.zoomSettleTimer = null;
        this.pendingSettledZoomLevel = null;
        this.zoomSettling = false;
        this.zoomLevel = nextSettledZoomLevel;
        this.measurementZoomLevel = nextSettledZoomLevel;
      });
      invalidate();
    }, MEASUREMENT_ZOOM_SETTLE_DELAY_MS);
  };

  @action
  setZoomLevel = (level: number, options?: { deferDerivedZoom?: boolean }) => {
    this.liveZoomLevel = level;
    if (this.camera) {
      this.camera.zoom = level;
      this.camera.updateProjectionMatrix();
    }

    if (options?.deferDerivedZoom) {
      this.scheduleSettledZoomLevelUpdate(level);
    } else {
      this.commitSettledZoomLevel(level);
    }
  }

  /**
   * Opens the freemium paywall modal with the given reason. The modal is
   * mounted from Main.tsx and observes these fields.
   *   - "shapes":       shown when a free user tries to add a shape past the cap
   *   - "project":      shown when a free user tries to create another floorplan
   *   - "download":     shown when a free user attempts to download the floorplan
   *   - "addToProject": shown when a free user attempts to add it to a BONI project
   */
  @action
  showPaywall = (
    reason: "shapes" | "project" | "download" | "addToProject",
  ) => {
    this.paywallReason = reason;
    this.paywallVisible = true;
  };

  @action
  hidePaywall = () => {
    this.paywallVisible = false;
    this.paywallReason = null;
  };

  interactionSnappingTolerance = (baseTolerance: number = this.snappingTolerance) => {
    const divisor = this.zoomLevelDivisor();
    const zoomScale = Number.isFinite(divisor) && divisor > 1 ? divisor : 1;
    return baseTolerance / zoomScale;
  }

  private current2DZoomLevel = () => {
    if (this.camera && Number.isFinite(this.camera.zoom)) {
      return this.camera.zoom;
    }
    return this.liveZoomLevel;
  };

  private clampInteractiveZoomLevel(targetZoom: number, currentZoom = this.current2DZoomLevel()) {
    if (!Number.isFinite(targetZoom)) {
      return currentZoom;
    }

    const minZoom = Math.min(MIN_INTERACTIVE_ZOOM_LEVEL, currentZoom);
    const maxZoom = Math.max(MAX_INTERACTIVE_ZOOM_LEVEL, currentZoom);
    return Math.max(minZoom, Math.min(maxZoom, targetZoom));
  }

  private wheelZoomSteps(event: WheelEvent) {
    const deltaModeMultiplier = event.deltaMode === 1
      ? 16
      : event.deltaMode === 2
        ? (typeof window !== "undefined" ? window.innerHeight : 800)
        : 1;
    const normalizedDeltaY = event.deltaY * deltaModeMultiplier;
    const steps = -normalizedDeltaY / WHEEL_DELTA_PER_ZOOM_STEP;
    return Math.max(-MAX_WHEEL_ZOOM_STEPS, Math.min(MAX_WHEEL_ZOOM_STEPS, steps));
  }

  zoomBySteps = (steps: number) => {
    if (this.view3D) {
      this.request3DZoom(steps);
      return;
    }

    if (this.camera && Number.isFinite(steps) && steps !== 0) {
      const currentZoom = this.current2DZoomLevel();
      const newZoom = this.clampInteractiveZoomLevel(
        currentZoom * Math.pow(ZOOM_STEP_FACTOR, steps),
        currentZoom
      );

      if (newZoom === currentZoom) {
        return;
      }

      this.setZoomLevel(newZoom, { deferDerivedZoom: true });
      invalidate();
    }
  };

  @action
  request3DZoom = (steps: number) => {
    if (!Number.isFinite(steps) || steps === 0) return;
    this.view3DZoomRequestSteps = steps;
    this.view3DZoomRequestId += 1;
  };

  @action
  request3DResetZoom = (bounds?: THREE.Box2 | null) => {
    this.view3DResetZoomBounds =
      bounds && !bounds.isEmpty() ? bounds.clone() : null;
    this.view3DResetZoomRequestId += 1;
  };

  /**
   * Take and clear one-shot 3D focus bounds for the pending reset-zoom.
   */
  @action
  consume3DResetZoomBounds = (): THREE.Box2 | null => {
    const bounds = this.view3DResetZoomBounds;
    this.view3DResetZoomBounds = null;
    return bounds;
  };

  zoomIn = () => {
    this.zoomBySteps(1);
  };

  zoomOut = () => {
    this.zoomBySteps(-1);
  };

  resetZoom = (options: ResetZoomOptions = {}): ResetZoomStats => {
    const resetStats: ResetZoomStats = {
      hasScene: false,
      hasValidBox: false,
      timestamp: Date.now(),
    };
    const shouldLogDetails = isFloorplannerDebugLoggingEnabled() || this.debugRenderingState;
    const shouldWarnOnInvalidBounds = options.warnOnInvalidBounds ?? true;

    if (this.view3D) {
      this.request3DResetZoom();
      this.lastResetZoomStats = resetStats;
      return resetStats;
    }

    if (!this.camera) {
      this.lastResetZoomStats = resetStats;
      return resetStats;
    }

    this.setZoomLevel(1);
    // Reset position
    const renderStore = getRenderStore();
    if (shouldLogDetails) {
      console.log("[resetZoom] Starting reset zoom calculation");
    }

    let box: THREE.Box3 | null = null;
    if (options.preferredBox) {
      box = options.preferredBox;
      resetStats.hasScene = Boolean(renderStore?.scene);
    } else if (renderStore?.scene) {
      resetStats.hasScene = true;
      if (shouldLogDetails) {
        console.log("[resetZoom] Scene exists, computing bounding box");
      }
      const bboxStartedAt = performance.now();
      box = renderStore.computeBoundingBoxForScene(renderStore.scene);
      const bboxMs = Math.round(performance.now() - bboxStartedAt);
      if (shouldLogDetails || bboxMs >= 50) {
        console.log("[load] 2D computeBoundingBoxForScene", {
          ms: bboxMs,
          meshCount: renderStore.lastBoundingBoxStats?.meshCount,
          validBoxCount: renderStore.lastBoundingBoxStats?.validBoxCount,
        });
      }
      const boundingBoxStats = renderStore.lastBoundingBoxStats;
      if (boundingBoxStats) {
        resetStats.meshCount = boundingBoxStats.meshCount;
        resetStats.validBoxCount = boundingBoxStats.validBoxCount;
      }
    }

    if (box || renderStore?.scene || options.preferredBox) {
      if (box && shouldLogDetails) {
        console.log("[resetZoom] Bounding box computed:", {
          min: box.min ? { x: box.min.x, y: box.min.y, z: box.min.z } : "null",
          max: box.max ? { x: box.max.x, y: box.max.y, z: box.max.z } : "null",
          isEmpty: box.isEmpty(),
          isValid: box.min && box.max &&
            Number.isFinite(box.min.x) && Number.isFinite(box.min.y) &&
            Number.isFinite(box.max.x) && Number.isFinite(box.max.y) &&
            box.min.x !== box.max.x && box.min.y !== box.max.y
        });
      } else if (!box && shouldWarnOnInvalidBounds) {
        console.warn("[resetZoom] Bounding box is null");
      }

      // Check if bounding box is valid (not empty and has finite values)
      if (box &&
        box.min && box.max &&
        Number.isFinite(box.min.x) && Number.isFinite(box.min.y) &&
        Number.isFinite(box.max.x) && Number.isFinite(box.max.y) &&
        box.min.x !== box.max.x && box.min.y !== box.max.y) {
        const center = box.getCenter(new THREE.Vector3());
        const size = new THREE.Vector3();
        box.getSize(size);
        resetStats.hasValidBox = true;
        resetStats.center = { x: center.x, y: center.y, z: center.z };
        resetStats.size = { x: size.x, y: size.y, z: size.z };

        if (shouldLogDetails) {
          console.log("[resetZoom] Bounding box center:", {
            x: center.x,
            y: center.y,
            z: center.z,
            sizeX: size.x.toFixed(2),
            sizeY: size.y.toFixed(2),
            isValid: Number.isFinite(center.x) && Number.isFinite(center.y)
          });
        }

        if (Number.isFinite(center.x) && Number.isFinite(center.y)) {
          // Fit the bounding box in the viewport. Orthographic 2D uses the
          // frustum at zoom=1; perspective (legacy / tests) uses FOV + distance.
          // Visible size scales as 1/zoom in both cases.
          let viewportHeight: number;
          let viewportWidth: number;
          if (this.camera instanceof THREE.OrthographicCamera) {
            viewportHeight = Math.abs(this.camera.top - this.camera.bottom);
            viewportWidth = Math.abs(this.camera.right - this.camera.left);
          } else {
            const perspectiveCamera = this.camera as THREE.PerspectiveCamera;
            const fov = perspectiveCamera.fov * (Math.PI / 180);
            const distance = Math.abs(perspectiveCamera.position.z - center.z) || 20;
            viewportHeight = 2 * distance * Math.tan(fov / 2);
            viewportWidth = viewportHeight * perspectiveCamera.aspect;
          }

          // Calculate zoom needed to fit the bounding box
          // We calculate the zoom required for both dimensions and take the smaller one
          // to ensure the entire bounding box fits within the viewport.
          // Using 0.6 padding (60% coverage) to be "more aggressive" and ensure visibility relative to UI panels
          const padding = 0.6;
          const zoomX = (viewportWidth * padding) / size.x;
          const zoomY = (viewportHeight * padding) / size.y;

          const calculatedZoom = Math.min(zoomX, zoomY);

          // Clamp zoom to reasonable values (0.01 to 100 for very small/large floorplans)
          const clampedZoom = Math.max(0.01, Math.min(100, calculatedZoom));

          if (shouldLogDetails) {
            console.log("[resetZoom] Calculated zoom:", {
              sizeX: size.x.toFixed(2),
              sizeY: size.y.toFixed(2),
              viewportWidth: viewportWidth.toFixed(2),
              viewportHeight: viewportHeight.toFixed(2),
              zoomX: zoomX.toFixed(4),
              zoomY: zoomY.toFixed(4),
              calculatedZoom: calculatedZoom.toFixed(4),
              clampedZoom: clampedZoom.toFixed(4)
            });
          }

          this.setZoomLevel(clampedZoom);
          if (shouldLogDetails) {
            console.log("[resetZoom] Setting camera position to:", { x: center.x, y: center.y, z: 20 });
          }
          this.camera.position.set(center.x, center.y, 20);
        } else {
          if (shouldWarnOnInvalidBounds) {
            console.warn("[resetZoom] Invalid bounding box center, using origin");
          }
          this.camera.position.set(0, 0, 20);
        }
      } else {
        if (shouldWarnOnInvalidBounds) {
          console.warn("[resetZoom] Invalid or empty bounding box, using origin. Details:", {
            boxExists: !!box,
            minExists: !!box?.min,
            maxExists: !!box?.max,
            minXValid: box?.min ? Number.isFinite(box.min.x) : false,
            minYValid: box?.min ? Number.isFinite(box.min.y) : false,
            maxXValid: box?.max ? Number.isFinite(box.max.x) : false,
            maxYValid: box?.max ? Number.isFinite(box.max.y) : false,
            hasWidth: box?.min && box?.max ? box.min.x !== box.max.x : false,
            hasHeight: box?.min && box?.max ? box.min.y !== box.max.y : false
          });
        }
        this.camera.position.set(0, 0, 20);
      }
    } else {
      if (shouldWarnOnInvalidBounds) {
        console.warn("[resetZoom] No scene available, using origin");
      }
      this.camera.position.set(0, 0, 20);
    }

    this.lastResetZoomStats = resetStats;
    invalidate();
    return resetStats;
  };

  zoomLevelDivisor = () => {
    // In preview mode, use fixed preview zoom level for consistent rendering
    if (this.previewMode) {
      return this.previewZoomLevel / 1.4;
    }
    // Return the divisor
    return this.zoomLevel / 1.4;
  }

  measurementZoomLevelDivisor = () => {
    if (this.previewMode) {
      return this.previewZoomLevel / 1.4;
    }
    return this.measurementZoomLevel / 1.4;
  }

  shouldShowMeasurements = () => {
    // Preview and exported renders should mirror the toolbar setting while
    // remaining independent of the editor's transient zoom-settling state.
    if (this.previewMode || isRenderStoreRendering()) {
      return this.showMeasures;
    }
    // During live wheel zoom the camera updates immediately, while measurement
    // geometry intentionally waits for the settled zoom. Hide stale overlays
    // for that short window instead of letting them resize late.
    if (this.zoomSettling) {
      return false;
    }
    // In editor canvas, always respect the toolbar showMeasures setting
    return this.showMeasures;
  }

  shouldShowSelectionHandles = () => {
    if (isLockedByOtherCollaborator()) {
      return false;
    }
    if (this.previewMode) {
      return true;
    }
    if (isRenderStoreRendering()) {
      return true;
    }
    // Selection handles also use deferred zoom-derived sizing. Hide them while
    // wheel zoom is settling so stale handle geometry is not shown.
    return !this.zoomSettling;
  }

  getMeasurementVisibilityScale = () => {
    // In preview mode, always use full scale for consistent rendering
    if (this.previewMode) {
      return 1.0;
    }
    // Always use full scale when rendering for export/download
    if (isRenderStoreRendering()) {
      return 1.0;
    }
    // Return a scale factor for measurement text size based on zoom level
    // Below 0.6 zoom: use a better scaling formula to prevent blurriness
    if (this.measurementZoomLevel < 0.6) {
      return Math.max(this.measurementZoomLevel / 0.8, 0.5);
    }
    return 1.0;
  }

  @action setPanning = (panning: boolean) => {
    // Locked viewers: keep 2D in hand-pan; keep 3D free for orbit.
    if (isLockedByOtherCollaborator()) {
      this.panning = !this.view3D;
      return;
    }
    this.panning = panning;
  }

  /** Activates panning while Space is held; restores prior state on release. */
  @action beginSpacePanning = () => {
    if (this.spacePanningActive) {
      return;
    }
    this.spacePanPreviousState = this.panning;
    this.spacePanningActive = true;
    this.setPanning(true);
  };

  @action endSpacePanning = () => {
    if (!this.spacePanningActive) {
      return;
    }
    const restorePanning = this.spacePanPreviousState ?? false;
    this.spacePanningActive = false;
    this.spacePanPreviousState = null;
    this.setPanning(restorePanning);
  };

  /**
   * Fully exits panning when switching to non-pan tools from UI controls.
   */
  @action stopPanningForToolInteraction = () => {
    this.spacePanningActive = false;
    this.spacePanPreviousState = null;
    this.setPanning(false);
  };

  @action setShiftPressed = (pressed: boolean) => {
    this.isShiftPressed = pressed;
  }

  @action setAltPressed = (pressed: boolean) => {
    this.isAltPressed = pressed;
  }

  @action setPreviewMode = (enabled: boolean, zoomLevel: number = 1.0) => {
    this.previewMode = enabled;
    this.previewZoomLevel = zoomLevel;
  }

  /**
   * Synchronize modifier key states from an event's shiftKey/altKey properties.
   * This corrects the state if keyup events were missed (e.g., window blur, 
   * focus on input fields, or other scenarios where keyboard events are lost).
   * Call this from pointermove/mousemove handlers to keep state in sync.
   */
  @action syncModifierKeys = (event: { shiftKey: boolean; altKey: boolean }) => {
    if (this.isShiftPressed !== event.shiftKey) {
      this.isShiftPressed = event.shiftKey;
    }
    if (this.isAltPressed !== event.altKey) {
      this.isAltPressed = event.altKey;
    }
  }

  // Debug visualization setters
  @action setDebugEdgeConnections = (enabled: boolean) => {
    this.debugEdgeConnections = enabled;
  };

  @action setDebugJointPolygons = (enabled: boolean) => {
    this.debugJointPolygons = enabled;
  };

  @action setDebugOutlineSegments = (enabled: boolean) => {
    this.debugOutlineSegments = enabled;
  };

  @action setDebugWallLengths = (enabled: boolean) => {
    this.debugWallLengths = enabled;
  };

  @action setDebugConnectionMetadata = (enabled: boolean) => {
    this.debugConnectionMetadata = enabled;
  };

  @action setDebugPerformance = (enabled: boolean) => {
    this.debugPerformance = enabled;
  };

  @action setDebugClippingPolygons = (enabled: boolean) => {
    this.debugClippingPolygons = enabled;
  };

  @action setDebugZIndexLayers = (enabled: boolean) => {
    this.debugZIndexLayers = enabled;
  };

  @action setDebugRenderingState = (enabled: boolean) => {
    this.debugRenderingState = enabled;
  };

  @action setDebugImaginaryLines = (enabled: boolean) => {
    this.debugImaginaryLines = enabled;
  };

  @action setDebugImaginaryIntersections = (enabled: boolean) => {
    this.debugImaginaryIntersections = enabled;
  };

  @action setDebugFillCorners = (enabled: boolean) => {
    this.debugFillCorners = enabled;
  };

  @action setDebugDisableWallGeometryCache = (enabled: boolean) => {
    this.debugDisableWallGeometryCache = enabled;
  };

  @action enableAllDebugVisualizations = () => {
    this.debugEdgeConnections = true;
    this.debugJointPolygons = true;
    this.debugOutlineSegments = true;
    this.debugWallLengths = true;
    this.debugConnectionMetadata = true;
    this.debugPerformance = true;
    this.debugClippingPolygons = true;
    this.debugZIndexLayers = true;
    this.debugRenderingState = true;
    this.debugImaginaryLines = true;
    this.debugImaginaryIntersections = true;
    this.debugFillCorners = true;
    this.debugDisableWallGeometryCache = false; // Keep cache enabled by default
  };

  @action disableAllDebugVisualizations = () => {
    this.debugEdgeConnections = false;
    this.debugJointPolygons = false;
    this.debugOutlineSegments = false;
    this.debugWallLengths = false;
    this.debugConnectionMetadata = false;
    this.debugPerformance = false;
    this.debugClippingPolygons = false;
    this.debugZIndexLayers = false;
    this.debugRenderingState = false;
    this.debugImaginaryLines = false;
    this.debugImaginaryIntersections = false;
    this.debugFillCorners = false;
    this.debugDisableWallGeometryCache = false; // Keep cache enabled by default
  };


  // Undo action
  undo = () => {
    if (isLockedByOtherCollaborator()) return;
    this.floorplannerStore.undo();
    // Material-only wall/floor undos need a demand-mode R3F frame.
    invalidate();
  };

  // Redo action
  redo = () => {
    if (isLockedByOtherCollaborator()) return;
    this.floorplannerStore.redo();
    invalidate();
  };

  copy = () => {
    if (isLockedByOtherCollaborator()) return;
    // Create a deep copy of selected elements with additional metadata
    const clipboardSelections = this.selections.filter(
      (selection): selection is WallType | SymbolType | SingleLineType | RulerLineType =>
        !isRuler3DType(selection as FloorplannerSelectionType),
    );
    this.clipboard = clipboardSelections.map(selection => {
      // Create a proper deep copy of the selection
      const copy = JSON.parse(JSON.stringify({
        ...selection,
        originalId: selection.id,
        position: selection.type === "wall" || selection.type === "singleLine" || selection.type === "rulerLine"
          ? { start: selection.start, end: selection.end }
          : { x: selection.position.x, y: selection.position.y }
      }));

      // Restore Vector2 instances
      if (copy.type === "wall" || copy.type === "singleLine" || copy.type === "rulerLine") {
        copy.start = toVector2(copy.position.start);
        copy.end = toVector2(copy.position.end);
        copy.controlPoint = toOptionalVector2(copy.controlPoint);
        if (Array.isArray(copy.edgeConnections)) {
          copy.edgeConnections = copy.edgeConnections.map(normalizeEdgeConnectionVectors);
        }
      } else {
        copy.position = toVector2(copy.position);
        if ((copy.type === "area" || copy.type === "areaFill") && Array.isArray(copy.vertices)) {
          copy.vertices = toVector2Array(copy.vertices);
        }
      }

      // For walls, store connected walls that are also selected
      if (isWallType(selection)) {
        // Copy centerConnections for walls where the connected wall is also selected
        (copy as ClipboardWall).selectedConnections = selection.centerConnections?.filter(conn =>
          clipboardSelections.some(sel => sel.id === conn.id)
        );

        // Copy edgeConnections for walls where the target wall is also selected
        (copy as ClipboardWall).selectedEdgeConnections = selection.edgeConnections?.filter(edgeConn =>
          clipboardSelections.some(sel => sel.id === edgeConn.targetWallId)
        ).map(normalizeEdgeConnectionVectors);
      }


      // For symbols, store wall attachment if the wall it's attached to is also selected
      if (isSymbolType(selection)) {
        const symbol = this.floorplannerStore.symbolsMap.get(selection.id);
        if (symbol && symbol.attachedTo) {
          // Only store the attachment if the wall it's attached to is also selected
          if (clipboardSelections.some(sel => sel.id === symbol.attachedTo)) {
            copy.attachedToOriginal = symbol.attachedTo;
          }
        }
      }

      return copy;
    });

    // Locked room floors are often not in the selection. Include them when every
    // wall of the bound room is copied so fill color / 3D materials follow paste.
    const selectedWallIds = new Set(
      clipboardSelections.filter(isWallType).map((wall) => wall.id),
    );
    if (selectedWallIds.size > 0) {
      const copiedSymbolOriginalIds = new Set(
        this.clipboard
          .filter((item) => isSymbolType(item))
          .map((item) => (item as ClipboardSymbol).originalId),
      );

      this.floorplannerStore.symbolsMap.forEach((symbol) => {
        if (!isAreaFillSymbolType(symbol) || !symbol.roomBinding?.objectIds?.length) {
          return;
        }
        if (copiedSymbolOriginalIds.has(symbol.id)) {
          return;
        }
        const boundWallIds = symbol.roomBinding.objectIds;
        if (!boundWallIds.every((wallId) => selectedWallIds.has(wallId))) {
          return;
        }

        const floorCopy = JSON.parse(JSON.stringify({
          ...symbol,
          originalId: symbol.id,
          position: { x: symbol.position.x, y: symbol.position.y },
        }));
        floorCopy.position = toVector2(floorCopy.position);
        if (Array.isArray(floorCopy.vertices)) {
          floorCopy.vertices = toVector2Array(floorCopy.vertices);
        }
        this.clipboard.push(floorCopy);
      });
    }

    // Reset lastPastePosition when copying new items
    this.lastPastePosition = null;
  };

  paste = () => {
    if (isLockedByOtherCollaborator()) return;
    if (this.clipboard.length === 0) return;

    // Freemium gate: reject the entire paste if it would exceed the user's
    // per-floorplan shape cap. We surface the paywall modal instead of
    // silently truncating so the user understands why nothing happened.
    if (!this.floorplannerStore.canAddShape(this.clipboard.length)) {
      this.showPaywall("shapes");
      return;
    }

    // Begin delta-based undo tracking for paste
    this.floorplannerStore.beginUndoTransaction('paste');

    // Block dirty state updates during the paste operation
    this.floorplannerStore.setBlockDirty(true);
    this.isPasting = true;

    try {
      // Calculate bounding box of clipboard items
      const clipboardBounds = this.calculateClipboardBounds(this.clipboard);

      // Calculate offset from original center to new center.
      const clipboardCenter = new THREE.Vector2(
        (clipboardBounds.min.x + clipboardBounds.max.x) / 2,
        (clipboardBounds.min.y + clipboardBounds.max.y) / 2
      );
      const pasteOffset = this.view3D
        ? new THREE.Vector2(PASTE_3D_OFFSET_METERS, PASTE_3D_OFFSET_METERS)
        : this.findOpenArea(clipboardBounds).sub(clipboardCenter);
      if (this.view3D) {
        this.lastPastePosition = clipboardCenter.clone().add(pasteOffset);
      }

      // Create a map to store original ID to new ID mappings
      const idMap = new Map<string, string>();

      // Keep track of newly created items for selection
      const newItems: Array<WallType | SymbolType | SingleLineType | RulerLineType> = [];

      transaction(() => {
        // First pass: Create all walls first
        this.clipboard.forEach(item => {
          if (isWallType(item)) {
            // Apply offset to both start and end points
            const newStart = toVector2(item.start).add(pasteOffset);
            const newEnd = toVector2(item.end).add(pasteOffset);
            const newControlPoint = toOptionalVector2(item.controlPoint)?.add(pasteOffset);

            // Strip clipboard-only metadata so it never persists into the floorplan JSON.
            const {
              originalId,
              selectedConnections,
              selectedEdgeConnections,
              // Legacy/clipboard artifacts that have ended up persisted in some floorplans
              position,
              connections,
              controlPoint,
              ...wallProps
            } = item as ClipboardWall & {
              position?: unknown;
              connections?: unknown;
            };
            void selectedConnections;
            void selectedEdgeConnections;
            void position;
            void connections;
            void controlPoint;

            const newWall: WallType = {
              ...wallProps,
              id: Math.random().toString(36).substr(2, 9),
              type: "wall",
              start: newStart,
              end: newEnd,
              controlPoint: newControlPoint,
              centerConnections: [], // Will be restored in second pass
              edgeConnections: [], // Clear edge connections - duplicated walls start unconnected
              symbolAttachments: [], // Will be restored in second pass
              roomIds: [], // Never carry over room membership from copied walls
            };

            this.floorplannerStore.wallsMap.set(newWall.id, newWall);
            idMap.set(originalId, newWall.id);
            newItems.push(newWall);
          }
        });

        // Second pass: Create all other objects
        this.clipboard.forEach(item => {
          if (!isWallType(item)) {
            let newId = '';
            let newItem: SymbolType | SingleLineType | RulerLineType | undefined;

            if (isSingleLineType(item) || isRulerLineType(item)) {
              // Apply offset to both start and end points
              const newStart = toVector2(item.start).add(pasteOffset);
              const newEnd = toVector2(item.end).add(pasteOffset);
              const newControlPoint = toOptionalVector2(item.controlPoint)?.add(pasteOffset);

              if (isSingleLineType(item)) {
                const { originalId, position, connections, controlPoint, ...lineProps } = item as ClipboardSingleLine & {
                  position?: unknown;
                  connections?: unknown;
                };
                void position;
                void connections;
                void controlPoint;
                const newLine: SingleLineType = {
                  ...lineProps,
                  id: Math.random().toString(36).substr(2, 9),
                  start: newStart,
                  end: newEnd,
                  controlPoint: newControlPoint,
                  centerConnections: [],
                  roomIds: [],
                  type: "singleLine"
                };
                this.floorplannerStore.singleLinesMap.set(newLine.id, newLine);
                newId = newLine.id;
                newItem = newLine;
                idMap.set(originalId, newId);
              } else {
                const { originalId, position, connections, controlPoint, ...lineProps } = item as ClipboardRulerLine & {
                  position?: unknown;
                  connections?: unknown;
                };
                void position;
                void connections;
                void controlPoint;
                const newLine: RulerLineType = {
                  ...lineProps,
                  id: Math.random().toString(36).substr(2, 9),
                  start: newStart,
                  end: newEnd,
                  controlPoint: newControlPoint,
                  centerConnections: [],
                  type: "rulerLine"
                };
                this.floorplannerStore.rulerLinesMap.set(newLine.id, newLine);
                newId = newLine.id;
                newItem = newLine;
                idMap.set(originalId, newId);
              }
            } else if (isSymbolType(item)) {
              // Apply offset to position
              const newPosition = toVector2(item.position).add(pasteOffset);

              // Create symbol without any attachment
              const {
                originalId: originalIdIgnored,
                attachedToOriginal: attachedToOriginalIgnored,
                position: positionIgnored,
                attachedTo: attachedToIgnored,
                ...symbolProps
              } = item;
              void originalIdIgnored;
              void attachedToOriginalIgnored;
              void positionIgnored;
              void attachedToIgnored;
              const normalizedSymbolProps = { ...symbolProps };
              if ((item.type === "area" || item.type === "areaFill") && Array.isArray((item as any).vertices)) {
                (normalizedSymbolProps as any).vertices = toVector2Array((item as any).vertices);
              }
              // Remap room-bound floor fills onto the newly pasted wall IDs so
              // drag/room sync can keep floors attached to the pasted room.
              const clipboardRoomBinding = (
                item as { roomBinding?: AreaFillSymbolType["roomBinding"] }
              ).roomBinding;
              if (item.type === "areaFill" && clipboardRoomBinding?.objectIds?.length) {
                const remappedObjectIds = clipboardRoomBinding.objectIds
                  .map((objectId) => idMap.get(objectId))
                  .filter((objectId): objectId is string => !!objectId);
                if (remappedObjectIds.length === clipboardRoomBinding.objectIds.length) {
                  (normalizedSymbolProps as { roomBinding?: AreaFillSymbolType["roomBinding"] }).roomBinding = {
                    ...clipboardRoomBinding,
                    objectIds: remappedObjectIds,
                    roomSignature: remappedObjectIds.slice().sort().join(","),
                  };
                } else {
                  delete (normalizedSymbolProps as { roomBinding?: unknown }).roomBinding;
                }
              }
              const newSymbol = {
                ...normalizedSymbolProps,
                id: Math.random().toString(36).substr(2, 9),
                position: newPosition,
                attachedTo: undefined,
              };

              this.floorplannerStore.symbolsMap.set(newSymbol.id, newSymbol);
              newId = newSymbol.id;
              newItem = newSymbol;
              idMap.set(item.originalId, newId);
            }

            // Add to new items array if item was created
            if (newItem) {
              newItems.push(newItem);
            }
          }
        });

        // After all symbols are created, preserve their relative z-index ordering
        const symbolsToPaste = this.clipboard.filter((item): item is ClipboardSymbol => isSymbolType(item));
        if (symbolsToPaste.length > 0) {
          // Find the maximum z-index in the current symbolsMap
          const maxZIndex = Math.max(...Array.from(this.floorplannerStore.symbolsMap.values()).map(s => s.zIndex));

          // Sort symbols by their original z-index
          symbolsToPaste.sort((a, b) => a.zIndex - b.zIndex);

          // Assign new z-indexes while preserving relative order
          symbolsToPaste.forEach((symbol, index) => {
            const newId = idMap.get(symbol.originalId);
            if (newId) {
              const newSymbol = this.floorplannerStore.symbolsMap.get(newId);
              if (newSymbol) {
                newSymbol.zIndex = maxZIndex + 1 + index;
              }
            }
          });
        }

        // Third pass: Restore connections and attachments
        const addCenterConnection = (wall: WallType, connection: WallConnectionType) => {
          if (!wall.centerConnections) {
            wall.centerConnections = [];
          }

          const existingIndex = wall.centerConnections.findIndex(existing =>
            existing.id === connection.id &&
            existing.sourcePosition === connection.sourcePosition &&
            existing.targetPosition === connection.targetPosition
          );

          if (existingIndex >= 0) {
            const existing = wall.centerConnections[existingIndex];
            if (!existing.mode && connection.mode) {
              const updated = [...wall.centerConnections];
              updated[existingIndex] = { ...existing, mode: connection.mode };
              wall.centerConnections = updated;
            }
            return;
          }

          wall.centerConnections.push(connection);
        };

        this.clipboard.forEach(item => {
          if (isWallType(item) && (item as ClipboardWall).selectedConnections) {
            const newWallId = idMap.get(item.originalId);
            if (!newWallId) return;

            (item as ClipboardWall).selectedConnections?.forEach(conn => {
              const newConnectedId = idMap.get(conn.id);
              if (!newConnectedId) return;

              // NOTE: Mid-point connections (T-junctions) are restored with their original
              // sourcePosition values. Since all walls are translated by the same offset
              // during paste, these relative distance values remain valid.
              // The fix in updateWallConnections.ts prevents these values from causing
              // corruption when walls are selected or whole-wall dragged.

              const sourceWall = this.floorplannerStore.wallsMap.get(newWallId);
              const targetWall = this.floorplannerStore.wallsMap.get(newConnectedId);

              if (sourceWall && targetWall) {
                const sourceConnection: WallConnectionType = {
                  id: newConnectedId,
                  sourcePosition: conn.sourcePosition,
                  targetPosition: conn.targetPosition,
                  mode: conn.mode,
                };

                const targetConnection: WallConnectionType = {
                  id: newWallId,
                  sourcePosition: conn.targetPosition,
                  targetPosition: conn.sourcePosition,
                  mode: conn.mode,
                };

                addCenterConnection(sourceWall, sourceConnection);
                addCenterConnection(targetWall, targetConnection);
              }
            });
          }

          // Restore edge connections with updated wall IDs
          if (isWallType(item) && (item as ClipboardWall).selectedEdgeConnections) {
            const newWallId = idMap.get(item.originalId);
            if (!newWallId) return;

            const sourceWall = this.floorplannerStore.wallsMap.get(newWallId);
            if (!sourceWall) return;

            (item as ClipboardWall).selectedEdgeConnections?.forEach(edgeConn => {
              const newTargetWallId = idMap.get(edgeConn.targetWallId);
              if (!newTargetWallId) return;

              const targetWall = this.floorplannerStore.wallsMap.get(newTargetWallId);
              if (!targetWall) return;

              // Initialize edgeConnections array if needed
              if (!sourceWall.edgeConnections) sourceWall.edgeConnections = [];

              // Create new edge connection with updated IDs
              // Edge points need to be translated by the paste offset
              const sourceEdgePoint = new THREE.Vector2(
                edgeConn.sourceEdgePoint.x + pasteOffset.x,
                edgeConn.sourceEdgePoint.y + pasteOffset.y
              );
              const targetEdgePoint = new THREE.Vector2(
                edgeConn.targetEdgePoint.x + pasteOffset.x,
                edgeConn.targetEdgePoint.y + pasteOffset.y
              );

              const newEdgeConnection: WallEdgeConnection = {
                id: Math.random().toString(36).substr(2, 9),
                sourceWallId: newWallId,
                targetWallId: newTargetWallId,
                sourceEndpoint: edgeConn.sourceEndpoint,
                targetEndpoint: edgeConn.targetEndpoint,
                sourceEdgePoint,
                targetEdgePoint,
                gapDistance: edgeConn.gapDistance,
                connectionAngle: edgeConn.connectionAngle,
                voidFill: edgeConn.voidFill,
              };

              sourceWall.edgeConnections.push(newEdgeConnection);
            });
          }


          if (isSymbolType(item)) {
            const attachedToOriginal = (item as ClipboardSymbol).attachedToOriginal;
            if (attachedToOriginal) {
              const newSymbolId = idMap.get(item.originalId);
              const newWallId = idMap.get(attachedToOriginal);

              if (!newSymbolId || !newWallId) {
                console.error('Failed to find new IDs for attachment restoration');
                return;
              }

              const symbol = this.floorplannerStore.symbolsMap.get(newSymbolId);
              const wall = this.floorplannerStore.wallsMap.get(newWallId);

              if (!symbol || !wall) {
                console.error('Failed to find symbol or wall for attachment');
                return;
              }

              // Update the symbol's attachment
              symbol.attachedTo = newWallId;

              // Update the wall's symbol attachments
              if (!wall.symbolAttachments) wall.symbolAttachments = [];

              // Calculate position from start as a ratio along the wall
              const wallDirection = wall.end.clone().sub(wall.start);
              const symbolOffset = symbol.position.clone().sub(wall.start);
              const positionFromStart = symbolOffset.dot(wallDirection.normalize());

              wall.symbolAttachments.push({
                symbolId: newSymbolId,
                positionFromStart,
              });
            }
          }
        });

        // Clear current selections and select all newly created items
        this.clearSelections();
        newItems.forEach(item => {
          if (isWallType(item)) {
            this.floorplannerStore.selectWall(item.id);
          } else if (isSymbolType(item)) {
            this.floorplannerStore.selectSymbol(item.id);
          } else if (isSingleLineType(item)) {
            this.floorplannerStore.selectLine(item.id);
          } else if (isRulerLineType(item)) {
            this.floorplannerStore.selectRuler(item.id);
          }
          this.addSelection(item);
        });

        // Re-attach symbols to walls to ensure proper rendering
        newItems.forEach(item => {
          if (isSymbolType(item)) {
            const symbol = this.floorplannerStore.symbolsMap.get(item.id);
            if (symbol && symbol.attachedTo) {
              const wall = this.floorplannerStore.wallsMap.get(symbol.attachedTo);
              if (wall) {
                // Temporarily detach and re-attach to force update
                const currentPosition = symbol.position.clone();
                this.floorplannerStore.detachSymbolFromWall(item.id);
                this.floorplannerStore.attachSymbolToWall(item.id, wall.id, [currentPosition.x, currentPosition.y]);
              }
            }
          }
        });

        // Force update all pasted walls to ensure proper rendering
        this.clipboard.forEach(item => {
          if (isWallType(item)) {
            const newWallId = idMap.get(item.originalId);
            if (newWallId) {
              this.floorplannerStore.objectNeedsUpdate(newWallId);
              this.floorplannerStore.markObjectForRoomUpdate(newWallId);
            }
          }
        });

        // Calculate bounds of pasted items
        const pastedBounds = {
          xMin: Infinity,
          xMax: -Infinity,
          yMin: Infinity,
          yMax: -Infinity
        };

        // Calculate bounds for all pasted items
        newItems.forEach(item => {
          if (isWallType(item) || isSingleLineType(item) || isRulerLineType(item)) {
            pastedBounds.xMin = Math.min(pastedBounds.xMin, item.start.x, item.end.x);
            pastedBounds.xMax = Math.max(pastedBounds.xMax, item.start.x, item.end.x);
            pastedBounds.yMin = Math.min(pastedBounds.yMin, item.start.y, item.end.y);
            pastedBounds.yMax = Math.max(pastedBounds.yMax, item.start.y, item.end.y);
          } else if (isSymbolType(item)) {
            const symbol = this.floorplannerStore.symbolsMap.get(item.id);
            if (symbol) {
              const dimensions = this.getSymbolDimensions(symbol);
              const halfWidth = dimensions.width / 2;
              const halfHeight = dimensions.height / 2;

              pastedBounds.xMin = Math.min(pastedBounds.xMin, symbol.position.x - halfWidth);
              pastedBounds.xMax = Math.max(pastedBounds.xMax, symbol.position.x + halfWidth);
              pastedBounds.yMin = Math.min(pastedBounds.yMin, symbol.position.y - halfHeight);
              pastedBounds.yMax = Math.max(pastedBounds.yMax, symbol.position.y + halfHeight);
            }
          }
        });

        this.pan2DCameraToBoundsIfOutsideView(pastedBounds);

        // Record all new items for undo
        newItems.forEach(item => {
          if (isSymbolType(item)) {
            this.floorplannerStore.recordSymbolChange(item.id, 'add');
          } else if (isWallType(item)) {
            this.floorplannerStore.recordWallChange(item.id, 'add');
          } else if (isSingleLineType(item) || isRulerLineType(item)) {
            this.floorplannerStore.recordObjectChange(item.id, 'add');
          }
        });
        this.floorplannerStore.pushToUndoStack();
      });
    } finally {
      // Re-enable dirty state updates after paste operation
      this.floorplannerStore.setBlockDirty(false);
      this.floorplannerStore.setDirty();
      invalidate(); // Force a re-render
      // Keep spinner up until queued wall geometry rebuilds finish (or fail).
      void this.flushPasteGeometry().finally(() => {
        runInAction(() => {
          this.isPasting = false;
        });
      });
    }
  };

  /**
   * Flush pending wall geometry after paste so shapes/joints settle before
   * clearing the paste overlay. Safe no-op when there are no pending updates.
   */
  private flushPasteGeometry = async (): Promise<void> => {
    try {
      const { wallUpdateManager } = await import('../utils/wallUpdateManager');
      // objectNeedsUpdate queues work via a dynamic-import .then; wait a tick
      // so those callbacks enqueue before we flush.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      if (typeof wallUpdateManager.processPendingUpdatesNow === 'function') {
        await wallUpdateManager.processPendingUpdatesNow();
      }
      // WallShape mount can enqueue a second wave after the first flush.
      await new Promise<void>(resolve => setTimeout(resolve, 0));
      if (typeof wallUpdateManager.processPendingUpdatesNow === 'function') {
        await wallUpdateManager.processPendingUpdatesNow();
      }
      // Rebuild rooms / bound floors after pasted walls settle.
      runInAction(() => {
        this.floorplannerStore.updateAffectedRooms();
      });
    } catch (error) {
      console.error('editorStore: Failed to flush wall updates after paste', error);
    }
  };

  duplicateSelected = () => {
    if (isLockedByOtherCollaborator()) return;
    this.copy();
    this.paste();
  }

  bringToFront = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      if (selection.type === "wall") {
        //this.floorplannerStore.bringWallToFront(selection.id);
      } else if (isSymbolType(selection) || isSingleLineType(selection)) {
        this.floorplannerStore.sendToFront(selection.id);
      }
    });
  }

  // Flip/flop methods for SVG symbols
  flipHorizontal = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      if (isSvgType(selection)) {
        // Get the current value from the store's symbol map
        const symbol = this.floorplannerStore.symbolsMap.get(selection.id);
        if (symbol && isSvgType(symbol)) {
          const currentFlip = symbol.flipHorizontal ?? false;
          this.floorplannerStore.updateSymbolProperty(selection.id, "flipHorizontal", !currentFlip);
        }
      }
    });
  }

  flipVertical = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      if (isSvgType(selection)) {
        // Get the current value from the store's symbol map
        const symbol = this.floorplannerStore.symbolsMap.get(selection.id);
        if (symbol && isSvgType(symbol)) {
          const currentFlip = symbol.flipVertical ?? false;
          this.floorplannerStore.updateSymbolProperty(selection.id, "flipVertical", !currentFlip);
        }
      }
    });
  }

  sendToBack = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      if (selection.type === "wall") {
        //this.floorplannerStore.sendWallToBack(selection.id);
      } else if (isSymbolType(selection) || isSingleLineType(selection)) {
        this.floorplannerStore.sendToBack(selection.id);
      }
    });
  }

  bringForward = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      if (selection.type === "wall") {
        //this.floorplannerStore.bringWallForward(selection.id);
      } else if (isSymbolType(selection) || isSingleLineType(selection)) {
        this.floorplannerStore.bringForward(selection.id);
      }
    });
  }

  bringBackward = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      if (selection.type === "wall") {
        //this.floorplannerStore.sendWallBackward(selection.id);
      } else if (isSymbolType(selection) || isSingleLineType(selection)) {
        this.floorplannerStore.bringBackward(selection.id);
      }
    });
  }
  /*   addText = () => {
      this.selections.forEach((selection) => {
          if (selection.type === "text") {
            this.floorplannerStore.addSymbol("text", [0, 0]);
          } 
      });
    }
       */


  // Method to get the visible bounds based on the current camera position and zoom level
  getVisibleBounds = (): ViewBounds2D | null => {
    if (!this.camera) return null;

    const aspectRatio = window.innerWidth / window.innerHeight;
    const zoomLevel = this.current2DZoomLevel();

    if (this.camera instanceof THREE.OrthographicCamera) {
      // For orthographic camera, the visible dimensions are affected by zoom
      const visibleWidth = (2 * this.camera.right) * (1 / zoomLevel);
      const visibleHeight = (2 * this.camera.top) * (1 / zoomLevel);

      // Calculate toolbar widths in world units based on the total visible width
      const leftToolbarWidthWorld = (180 / window.innerWidth) * visibleWidth;
      const rightToolbarWidthWorld = (250 / window.innerWidth) * visibleWidth;

      // Calculate half dimensions
      const halfWidth = visibleWidth / 2;
      const halfHeight = visibleHeight / 2;

      return {
        xMin: this.camera.position.x - halfWidth + leftToolbarWidthWorld,
        xMax: this.camera.position.x + halfWidth - rightToolbarWidthWorld,
        yMin: this.camera.position.y - halfHeight,
        yMax: this.camera.position.y + halfHeight
      };
    } else if (this.camera instanceof THREE.PerspectiveCamera) {
      // For perspective camera, calculate based on FOV and distance
      const distance = Math.abs(this.camera.position.z);
      const halfFov = THREE.MathUtils.degToRad(this.camera.fov / 2);
      const halfHeight = (distance * Math.tan(halfFov)) * (1 / zoomLevel);
      const halfWidth = halfHeight * aspectRatio;

      // Calculate toolbar widths in world units
      const totalWidth = 2 * halfWidth;
      const leftToolbarWidthWorld = (180 / window.innerWidth) * totalWidth;
      const rightToolbarWidthWorld = (250 / window.innerWidth) * totalWidth;

      return {
        xMin: this.camera.position.x - halfWidth + leftToolbarWidthWorld,
        xMax: this.camera.position.x + halfWidth - rightToolbarWidthWorld,
        yMin: this.camera.position.y - halfHeight,
        yMax: this.camera.position.y + halfHeight
      };
    }

    return null;
  };

  private pan2DCameraToBoundsIfOutsideView = (bounds: ViewBounds2D) => {
    if (this.view3D || !this.camera) return;
    if (
      !Number.isFinite(bounds.xMin) ||
      !Number.isFinite(bounds.xMax) ||
      !Number.isFinite(bounds.yMin) ||
      !Number.isFinite(bounds.yMax)
    ) {
      return;
    }

    const visibleBounds = this.getVisibleBounds();
    if (!visibleBounds) return;

    const isOutsideView =
      bounds.xMin < visibleBounds.xMin ||
      bounds.xMax > visibleBounds.xMax ||
      bounds.yMin < visibleBounds.yMin ||
      bounds.yMax > visibleBounds.yMax;

    if (!isOutsideView) return;

    this.camera.position.x = (bounds.xMin + bounds.xMax) / 2;
    this.camera.position.y = (bounds.yMin + bounds.yMax) / 2;
    invalidate();
  };

  private getPasteSearchOrigin = (clipboardBounds: { min: THREE.Vector2, max: THREE.Vector2 }) => {
    if (this.view3D) {
      const [visibleCenterX, visibleCenterY] = this.getVisibleCenter();
      if (Number.isFinite(visibleCenterX) && Number.isFinite(visibleCenterY)) {
        return new THREE.Vector2(visibleCenterX, visibleCenterY);
      }
    }

    return new THREE.Vector2(
      clipboardBounds.min.x,
      clipboardBounds.min.y
    );
  };


  // Helper to calculate the center of the visible area
  getVisibleCenter = () => {
    if (this.view3D) {
      const visible3DCenter = this.getVisible3DCenter();
      if (visible3DCenter) return visible3DCenter;
    }

    const visibleBounds = this.getVisibleBounds();
    if (!visibleBounds) return [0, 0];

    const centerX = (visibleBounds.xMin + visibleBounds.xMax) / 2;
    const centerY = (visibleBounds.yMin + visibleBounds.yMax) / 2;
    return [centerX, centerY];
  };

  private getVisible3DCenter = (): [number, number] | null => {
    if (!this.camera) return null;

    const canvas =
      typeof document !== "undefined"
        ? document.getElementById(FLOORPLANNER_CANVAS_ID)
        : null;
    const rect = canvas?.getBoundingClientRect();

    if (rect && rect.width > 0 && rect.height > 0) {
      const screenX =
        rect.left +
        rect.width / 2 +
        (LEFT_INSERTION_MARGIN_PX - RIGHT_INSERTION_MARGIN_PX) / 2;
      const screenY = rect.top + rect.height / 2;
      const projectedCenter = this.projectScreenPointToBasePlane(
        screenX,
        screenY,
        rect,
      );
      if (projectedCenter) return projectedCenter;
    }

    return this.projectCameraNdcToBasePlane(new THREE.Vector2(0, 0));
  };

  private projectScreenPointToBasePlane = (
    screenX: number,
    screenY: number,
    rect: DOMRect,
  ): [number, number] | null => {
    const ndc = new THREE.Vector2(
      ((screenX - rect.left) / rect.width) * 2 - 1,
      -((screenY - rect.top) / rect.height) * 2 + 1,
    );
    return this.projectCameraNdcToBasePlane(ndc);
  };

  private projectCameraNdcToBasePlane = (
    ndc: THREE.Vector2,
  ): [number, number] | null => {
    if (!this.camera) return null;

    this.camera.updateMatrixWorld(true);
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(ndc, this.camera);
    const basePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersection = raycaster.ray.intersectPlane(
      basePlane,
      new THREE.Vector3(),
    );

    if (
      !intersection ||
      !Number.isFinite(intersection.x) ||
      !Number.isFinite(intersection.y)
    ) {
      return null;
    }

    return [intersection.x, intersection.y];
  };


  handleKeyDown = (event: KeyboardEvent) => {
    const activeElement = document.activeElement;
    const isInputField =
      (activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        (activeElement?.getAttribute("contenteditable") === "true"));

    // Always handle shift key regardless of input field focus
    if (event.key === 'Shift') {
      this.setShiftPressed(true);
    }

    // Always handle alt/option key regardless of input field focus
    if (event.key === 'Alt') {
      this.setAltPressed(true);
    }

    if (
      event.code === "Space" &&
      !isInputField &&
      !event.repeat &&
      !this.downloadProjectVisible &&
      !this.addToPresentationVisible
    ) {
      event.preventDefault();
      this.beginSpacePanning();
      return;
    }

    if (this.ruler3DConstructionMode && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.setRuler3DConstructionMode(false);
      return;
    }

    if (this.view3D && event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.setView3D(false);
      return;
    }

    const isZKey = event.key.toLowerCase() === "z" || event.code === "KeyZ";
    const isUndoShortcut = isZKey && !event.shiftKey && !event.altKey &&
      (event.ctrlKey || event.metaKey);

    if (this.wallConstructionMode && isUndoShortcut) {
      event.preventDefault();
      event.stopPropagation();
      this.requestWallConstructionUndoLastWall();
      return;
    }

    // Always handle some keys regardless of input field focus
    if ((this.wallConstructionMode || editorStore.lineConstructionMode || editorStore.rulerConstructionMode || editorStore.ruler3DConstructionMode || editorStore.areaConstructionMode || editorStore.areaFillConstructionMode || editorStore.areaBleedFillMode) && event.key === "Escape") {
      event.preventDefault(); // Prevent any default behavior of the input field
      this.exitConstructionModes();
    } else if (this.wallConstructionMode && event.key === "Enter") {
      this.wallConstructionModeAddNewWall = true
    } else if (this.lineConstructionMode && event.key === "Enter") {
      this.lineConstructionModeAddNewLine = true
    } else if (this.rulerConstructionMode && event.key === "Enter") {
      this.rulerConstructionModeAddNewLine = true
    }
    else if ((this.areaConstructionMode || this.areaFillConstructionMode) && event.key === "Enter") {
      this.areaConstructionModeAddPoint = true
    }
    // Only handle other shortcuts if not focused on input field
    if (!isInputField) {
      if (event.ctrlKey || event.metaKey) {
        if (event.key === "z") {
          // Ctrl+Z or Cmd+Z
          event.preventDefault();
          this.undo();
        } else if (event.key === "y" || (event.shiftKey && event.key === "z")) {
          // Ctrl+Y or Ctrl+Shift+Z (or Cmd+Shift+Z on macOS)
          event.preventDefault();
          this.redo();
        } else if (event.key === "c") {
          // Ctrl+C or Cmd+C
          event.preventDefault();
          this.copy();
        } else if (event.key === "v") {
          // Ctrl+V or Cmd+V
          event.preventDefault();
          this.paste();
        }
      }

      // Handle zoom in and zoom out with keyboard shortcuts
      if (event.key === "=" || event.key === "+") {
        event.preventDefault();
        this.zoomIn();
      } else if (event.key === "-") {
        event.preventDefault();
        this.zoomOut();
      } else if (event.key === "0") {
        event.preventDefault();
        this.resetZoom();
      }

      const blockSelectionKeyboardMove =
        this.spacePanningActive ||
        this.wallConstructionMode ||
        this.lineConstructionMode ||
        this.rulerConstructionMode ||
        this.ruler3DConstructionMode ||
        this.areaConstructionMode ||
        this.areaFillConstructionMode ||
        this.areaBleedFillMode;

      // Handle movement with arrow keys
      if (!blockSelectionKeyboardMove) {
        let movementOffset;
        if (event.shiftKey) {
          movementOffset = this.movementOffsetFine;
        } else {
          movementOffset = this.movementOffsetCorse;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          this.moveSelections(0, movementOffset); // Move up
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          this.moveSelections(0, -movementOffset); // Move down
        } else if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.moveSelections(-movementOffset, 0); // Move left
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          this.moveSelections(movementOffset, 0); // Move right
        }
      }

      // H toggles temporary 3D hide/show for selected walls (session-only).
      // Works in 2D and 3D so walls can be pre-hidden before entering 3D.
      // With no wall selected, H restores all hidden walls so users can recover
      // after clicking away from an invisible mesh.
      if (
        !event.repeat &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        (event.key === "h" || event.key === "H" || event.code === "KeyH")
      ) {
        if (this.toggleSelectedWallsHiddenIn3D()) {
          event.preventDefault();
          return;
        }
        if (this.hiddenWallIds.size > 0) {
          event.preventDefault();
          this.clearHiddenWalls();
          return;
        }
        // No selected walls and nothing hidden — fall through (e.g. other shortcuts).
      }

      if (this.view3D) {
        return;
      }

      // Construction mode shortcuts
      if (event.key === "w") {
        this.setWallConstructionMode(!this.wallConstructionMode);
      } else if (event.key === "l") {
        this.setLineConstructionMode(!this.lineConstructionMode);
      }
      else if (event.key === "r") {
        this.setRulerConstructionMode(!this.rulerConstructionMode);
      }
      else if (event.key === "a") {
        this.setAreaConstructionMode(!this.areaConstructionMode);
      }
    }
  };

  handleBeforeInput = (event: InputEvent) => {
    if (this.wallConstructionMode && event.inputType === "historyUndo") {
      event.preventDefault();
      this.requestWallConstructionUndoLastWall();
    }
  };

  handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      this.setShiftPressed(false);
    }
    if (event.key === 'Alt') {
      this.setAltPressed(false);
    }
    if (event.code === "Space") {
      this.endSpacePanning();
    }
  }

  /**
   * Global pointer move handler that syncs modifier key states.
   * This fixes the "stuck shift key" issue where keyup events can be missed
   * (e.g., when switching tabs, focus changes, or during drag operations).
   */
  handleGlobalPointerMove = (event: PointerEvent) => {
    this.syncModifierKeys(event);
  }

  // Handle mouse wheel/trackpad zoom events
  handleWheel = (event: WheelEvent) => {
    // Block wheel events when modals are open to prevent canvas interaction behind modal
    if (this.downloadProjectVisible || this.addToPresentationVisible) {
      return;
    }

    if (this.camera && !this.view3D) {
      event.preventDefault();
      this.zoomBySteps(this.wheelZoomSteps(event));
    }
  };

  handleMouseDown = (event: MouseEvent) => {
    // Check if the third mouse button (middle mouse button) is pressed
    if (event.button === 1) {
      // Store the current panning state
      this.previousPanningState = this.panning;
      // Enable panning
      this.setPanning(true);
    }
  };

  handleMouseUp = () => {
  };

  /**
   * Disarm every drawing tool at once. Used when leaving the canvas in a state
   * where no construction may continue, such as entering collaborator lock.
   */
  exitConstructionModes = () => {
    this.wallConstructionMode = false;
    this.wallConstructionSeedStart = null;
    this.wallConstructionFromHoverAdd = false;
    this.lineConstructionMode = false;
    this.rulerConstructionMode = false;
    this.ruler3DConstructionMode = false;
    this.areaConstructionMode = false;
    this.areaFillConstructionMode = false;
    this.areaBleedFillMode = false;
  }

  setWallConstructionMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.wallConstructionMode = mode;
    if (mode) {
      // Drawing walls must not leave the canvas stuck in pan mode (e.g. blue "+").
      this.stopPanningForToolInteraction();
      this.lineConstructionMode = false;
      this.rulerConstructionMode = false;
      this.ruler3DConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaFillConstructionMode = false;
      this.areaBleedFillMode = false;
    } else {
      this.wallConstructionSeedStart = null;
      this.wallConstructionFromHoverAdd = false;
    }
  }

  /**
   * Enter wall construction with the first click already placed on an existing wall
   * (from the hover "+" affordance).
   */
  startWallConstructionFromPoint = (
    start: THREE.Vector2,
    sourceWallId: string,
    attachEndpoint: "start" | "end" | "middle",
    projectedPoint: THREE.Vector2,
    outwardDirection: THREE.Vector2,
  ) => {
    if (isLockedByOtherCollaborator()) return;
    if (this.view3D) return;
    const outward = outwardDirection.clone();
    if (outward.lengthSq() > 1e-12) {
      outward.normalize();
    } else {
      outward.set(1, 0);
    }
    this.wallConstructionSeedStart = {
      x: start.x,
      y: start.y,
      sourceWallId,
      attachEndpoint,
      projectedX: projectedPoint.x,
      projectedY: projectedPoint.y,
      outwardX: outward.x,
      outwardY: outward.y,
    };
    this.wallConstructionSeedRequestId += 1;
    this.wallConstructionFromHoverAdd = true;
    this.setWallConstructionMode(true);
  }

  clearWallConstructionSeedStart = () => {
    this.wallConstructionSeedStart = null;
  }

  setWallConstructionModeAddNewWall = (mode: boolean) => {
    this.wallConstructionModeAddNewWall = mode;
  }

  requestWallConstructionUndoLastWall = () => {
    if (isLockedByOtherCollaborator()) return;
    const now = Date.now();
    if (now - this.lastWallConstructionUndoLastWallRequestAt < 50) {
      return;
    }
    this.lastWallConstructionUndoLastWallRequestAt = now;
    this.wallConstructionModeUndoLastWallRequest += 1;
  }

  setLineConstructionMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.lineConstructionMode = mode;
    if (mode) {
      this.wallConstructionMode = false;
      this.rulerConstructionMode = false;
      this.ruler3DConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaFillConstructionMode = false;
      this.areaBleedFillMode = false;
    }
  }

  setLineConstructionModeAddNewLine = (mode: boolean) => {
    this.lineConstructionModeAddNewLine = mode;
  }
  setRulerConstructionMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.rulerConstructionMode = mode;
    if (mode) {
      this.wallConstructionMode = false;
      this.lineConstructionMode = false;
      this.ruler3DConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaFillConstructionMode = false;
      this.areaBleedFillMode = false;

    }
  }

  setRuler3DConstructionMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.ruler3DConstructionMode = mode;
    if (mode) {
      this.wallConstructionMode = false;
      this.lineConstructionMode = false;
      this.rulerConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaFillConstructionMode = false;
      this.areaBleedFillMode = false;
    }
  }

  setRulerConstructionModeAddNewLine = (mode: boolean) => {
    this.rulerConstructionModeAddNewLine = mode;
  }

  setAreaConstructionMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.areaConstructionMode = mode;
    if (mode) {
      this.wallConstructionMode = false;
      this.rulerConstructionMode = false;
      this.ruler3DConstructionMode = false;
      this.lineConstructionMode = false;
      this.areaFillConstructionMode = false;
      this.areaBleedFillMode = false;
    }
  }

  setAreaFillConstructionMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.areaFillConstructionMode = mode;
    if (mode) {
      this.stopPanningForToolInteraction();
      this.wallConstructionMode = false;
      this.rulerConstructionMode = false;
      this.ruler3DConstructionMode = false;
      this.lineConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaBleedFillMode = false;
    }
  }

  setAreaBleedFillMode = (mode: boolean) => {
    if (mode && isLockedByOtherCollaborator()) return;
    this.areaBleedFillMode = mode;
    if (mode) {
      this.stopPanningForToolInteraction();
      this.wallConstructionMode = false;
      this.rulerConstructionMode = false;
      this.ruler3DConstructionMode = false;
      this.lineConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaFillConstructionMode = false;
    }
  }

  setAreaConstructionModeAddPoint = (mode: boolean) => {
    this.areaConstructionModeAddPoint = mode;
  }

  requestOpenFillProperties = () => {
    this.fillPropertiesOpenRequest += 1;
  }

  /**
   * Ask the left sidebar to open Product Library (anchored to its initiator).
   * Used by the empty Product List CTA so placement stays consistent.
   */
  requestOpenProductLibrary = () => {
    this.productLibraryOpenRequest += 1;
  }

  setWallEditingOuterLength = (wallId: string | undefined) => {
    this.wallEditingOuterLength = wallId
  }
  setWallEditingInnerLength = (wallId: string | undefined) => {
    this.wallEditingInnerLength = wallId
  }

  /**
   * Set the wall ID that has an attachment (door/window/void) being dragged on it.
   * Used to hide the wall's measurement line during attachment drag to avoid overlap.
   */
  setAttachmentDraggingOnWall = (wallId: string | null) => {
    this.attachmentDraggingOnWall = wallId || undefined;
  }

  setWallEditingLengthPanel = (outerLength: number | undefined) => {
    this.wallEditingLengthPanel = outerLength
  }

  setShowMeasures = (show: boolean) => {
    this.showMeasures = show;
  }

  /** Whether a wall is temporarily hidden in the 3D view (session-only). */
  isWallHidden = (wallId: string): boolean => {
    return this.hiddenWallIds.has(wallId);
  }

  /**
   * Hide or show walls in 3D only. Does not mutate wall data or persistence.
   */
  @action
  setWallsHiddenIn3D = (wallIds: string[], hidden: boolean) => {
    if (wallIds.length === 0) return;
    wallIds.forEach((wallId) => {
      if (hidden) {
        this.hiddenWallIds.add(wallId);
      } else {
        this.hiddenWallIds.delete(wallId);
      }
    });
    invalidate();
  }

  /** Restore all temporarily hidden walls in 3D. */
  @action
  clearHiddenWalls = () => {
    if (this.hiddenWallIds.size === 0) return;
    this.hiddenWallIds.clear();
    invalidate();
  }

  /**
   * Toggle 3D visibility for currently selected walls.
   * @returns true if any selected walls were toggled
   */
  @action
  toggleSelectedWallsHiddenIn3D = (): boolean => {
    const wallIds = this.selections.filter(isWallType).map((wall) => wall.id);
    if (wallIds.length === 0) return false;
    const allHidden = wallIds.every((wallId) => this.hiddenWallIds.has(wallId));
    this.setWallsHiddenIn3D(wallIds, !allHidden);
    return true;
  }

  /**
   * Toggle or set the length display unit. Persists to localStorage only
   * (does not mutate floorplan geometry or serialization).
   */
  setDisplayLengthUnit = (unit: LengthUnit) => {
    this.displayLengthUnit = unit;
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem(DISPLAY_LENGTH_UNIT_STORAGE_KEY, unit);
      }
    } catch {
      // Ignore quota / private-mode failures; preference still applies in-session.
    }
  }

  /** Flip between mm and inches. */
  toggleDisplayLengthUnit = () => {
    this.setDisplayLengthUnit(this.displayLengthUnit === "mm" ? "in" : "mm");
  }
  setTextEditing = (text: string | undefined) => {
    this.textEditing = text
  }

  setSingleLineEditingLength = (length: string | undefined) => {
    this.singleLineEditingLength = length
  }

  setSingleLineEditingLengthPanel = (lineLength: number | undefined) => {
    this.singleLineEditingLengthPanel = lineLength
  }

  setRulerLineEditingLength = (length: string | undefined) => {
    this.rulerLineEditingLength = length
  }
  setAreaEditingLength = (length: string | undefined) => {
    this.areaEditingLength = length
  }

  setRulerEditingLengthPanel = (rulerLength: number | undefined) => {
    this.rulerEditingLengthPanel = rulerLength
  }

  setTextShow = (show: boolean) => {
    this.textShow = show;
  }

  setSelections = (selections: Array<WallType | SymbolType>) => {
    // Loop through selections and select them in the floorplanner
    this.floorplannerStore.unSelectAll();
    selections.forEach((selection) => {
      switch (selection.type) {
        case "wall":
          this.floorplannerStore.selectWall(selection.id);
          break;
        case "symbol":
        case "door":
        case "slidingDoor":
        case "doubleDoor":
        case "window":
        case "circleStairs":
        case "rectStairs":
        case "turnStairs":
        case "square":
        case "circle":
        case "triangle":
        case "polygon":
        case "svg":
        case "text":
          this.floorplannerStore.selectSymbol(selection.id);
          break;
        default:
          console.error("Unknown selection type", selection);
      }
    });
    this.selections = selections;
  };

  clearSelections = () => {
    if (this.selections.length > 0) {
      this.floorplannerStore.unSelectObjects(this.selections.map((selection) => selection.id));
    } else {
      this.floorplannerStore.unSelectAll();
    }
    this.selections = [];
  };

  /**
   * Mark an object as selected in the floorplanner store.
   * Selection handles (e.g. WallShape) gate on the object's `.selected` flag,
   * so editorStore.selections alone is not enough for handle visibility.
   */
  private markFloorplannerSelection = (selection: FloorplannerSelectionType) => {
    if (isWallType(selection)) {
      this.floorplannerStore.selectWall(selection.id);
    } else if (isSymbolType(selection)) {
      this.floorplannerStore.selectSymbol(selection.id);
    } else if (isSingleLineType(selection)) {
      this.floorplannerStore.selectLine(selection.id);
    } else if (isRulerLineType(selection)) {
      this.floorplannerStore.selectRuler(selection.id);
    } else if (isRuler3DType(selection)) {
      this.floorplannerStore.selectRuler3D(selection.id);
    }
  };

  addSelection = (selection: FloorplannerSelectionType) => {
    if (this.selections.findIndex((s) => s.id === selection.id) === -1) {
      this.selections.push(selection);
    }

    // Keep floorplanner `.selected` in sync so selection handles render after
    // create/drag paths that only call addSelection (not selectWall/etc).
    this.markFloorplannerSelection(selection);

    // Check if this object is part of a group and add all group members
    const groupId = this.floorplannerStore.getObjectGroup(selection.id);
    if (groupId) {
      const groupObjects = this.floorplannerStore.getGroupObjects(groupId);
      groupObjects.forEach(groupObj => {
        if (this.selections.findIndex((s) => s.id === groupObj.id) === -1) {
          const groupSelection = groupObj as FloorplannerSelectionType;
          this.selections.push(groupSelection);
          this.markFloorplannerSelection(groupSelection);
        }
      });
    }
  };

  removeSelection = (selection: FloorplannerSelectionType) => {
    const index = this.selections.findIndex((s) => s.id === selection.id);
    if (index > -1) {
      this.selections.splice(index, 1);
    }
  };

  // Method to move selected elements by a given offset
  moveSelections = (xOffset: number, yOffset: number) => {
    if (isLockedByOtherCollaborator()) return;
    if (xOffset === 0 && yOffset === 0) return;
    if (this.selections.length === 0) return;

    const store = this.floorplannerStore;
    const delta = new THREE.Vector2(xOffset, yOffset);
    const selectedWallIds = new Set(
      this.selections.filter(isWallType).map((selection) => selection.id),
    );
    const rigidGroupWallTranslate = selectedWallIds.size >= 2;

    store.beginUndoTransaction("keyboardNudge");
    this.selections.forEach((selection) => {
      store.recordObjectForUndo(selection.id);
      if (isSymbolType(selection)) {
        const attachedWall = store.symbolIsAttached(selection.id);
        if (attachedWall && !selectedWallIds.has(attachedWall)) {
          store.recordWallForUndo(attachedWall);
        }
      }
    });

    const affectedWallIds = new Set<string>();
    const translatedObjectIds = new Set<string>();

    if (rigidGroupWallTranslate) {
      store
        .disconnectSelectionFromExternalConnections(selectedWallIds)
        .forEach((wallId) => affectedWallIds.add(wallId));
    }

    transaction(() => {
      this.selections.forEach((selection) => {
        const selectable = selection as FloorplannerSelectionType;
        if (isWallType(selectable) || isSingleLineType(selectable)) {
          if (rigidGroupWallTranslate) {
            const object = store.findObjectId(selection.id) as
              | WallType
              | SingleLineType
              | undefined;
            if (!object) return;
            const start = object.start.clone().add(delta);
            const end = object.end.clone().add(delta);
            store.updateObjectPosition(object.id, start, end, {
              bypassEdgeConstraints: true,
            });
            store.markObjectForRoomUpdate(object.id);
            translatedObjectIds.add(object.id);
            return;
          }

          const object = store.findObjectId(selection.id) as
            | WallType
            | SingleLineType
            | undefined;
          if (!object) return;
          translateWallOrLineByWorldDelta(object, delta).forEach((objectId) => {
            translatedObjectIds.add(objectId);
            if (store.wallsMap.has(objectId)) {
              affectedWallIds.add(objectId);
            }
            store.markObjectForRoomUpdate(objectId);
          });
        } else if (isRulerLineType(selectable)) {
          const object = store.findObjectId(selection.id) as RulerLineType | undefined;
          if (!object) return;
          const start = object.start.clone().add(delta);
          const end = object.end.clone().add(delta);
          store.updateObjectPosition(object.id, start, end, {
            bypassEdgeConstraints: true,
          });
          store.markObjectForRoomUpdate(object.id);
          translatedObjectIds.add(object.id);
          updateWallConnections(object.id, "middleV", [object.id], store);
        } else if (isSymbolType(selectable)) {
          const symbol = store.symbolsMap.get(selection.id);
          if (!symbol) return;
          if (symbol.attachedTo && selectedWallIds.has(symbol.attachedTo)) {
            return;
          }
          if (symbol.attachedTo) {
            const wall = store.wallsMap.get(symbol.attachedTo);
            const attachment = wall?.symbolAttachments?.find(
              (a) => a.symbolId === symbol.id,
            );
            if (wall && attachment) {
              const { tangent } = evaluateWallAt(wall, attachment.positionFromStart);
              const offsetVec = new THREE.Vector2(xOffset, yOffset);
              let slideDelta = offsetVec.dot(tangent);

              if (Math.abs(slideDelta) < 1e-5) {
                slideDelta =
                  Math.abs(xOffset) > Math.abs(yOffset) ? xOffset : yOffset;
              }

              const newPosFromStart = Math.max(
                0,
                Math.min(
                  attachment.positionFromStart + slideDelta,
                  getWallArcLength(wall),
                ),
              );
              const { point: newWorldPos } = evaluateWallAt(wall, newPosFromStart);

              store.updateSymbolAttachmentPosition(
                symbol.id,
                wall.id,
                [newWorldPos.x, newWorldPos.y],
                false,
              );
            }
          } else {
            store.updateSymbolProperty(selection.id, "position", [
              symbol.position.x + xOffset,
              symbol.position.y + yOffset,
            ]);
          }
        } else if (isRuler3DType(selectable)) {
          const ruler = store.rulers3DMap.get(selectable.id);
          if (!ruler) return;
          store.updateRuler3D({
            ...ruler,
            start: new THREE.Vector3(
              ruler.start.x + xOffset,
              ruler.start.y + yOffset,
              ruler.start.z,
            ),
            end: new THREE.Vector3(
              ruler.end.x + xOffset,
              ruler.end.y + yOffset,
              ruler.end.z,
            ),
          });
        }
      });

      if (rigidGroupWallTranslate) {
        selectedWallIds.forEach((wallId) => {
          const wall = store.wallsMap.get(wallId);
          if (!wall?.edgeConnections?.length) return;
          wall.edgeConnections = wall.edgeConnections.map((connection) => ({
            ...connection,
            sourceEdgePoint: new THREE.Vector2(
              connection.sourceEdgePoint.x + delta.x,
              connection.sourceEdgePoint.y + delta.y,
            ),
            targetEdgePoint: new THREE.Vector2(
              connection.targetEdgePoint.x + delta.x,
              connection.targetEdgePoint.y + delta.y,
            ),
          }));
          store.wallsMap.set(wallId, wall);
        });
      }
    });

    if (rigidGroupWallTranslate) {
      selectedWallIds.forEach((wallId) => {
        updateWallConnections(wallId, null, [...selectedWallIds], store);
      });
    } else {
      affectedWallIds.forEach((wallId) => {
        updateWallConnections(wallId, "middleV", [...affectedWallIds], store);
      });
      this.selections.forEach((selection) => {
        if (isSingleLineType(selection) && !affectedWallIds.has(selection.id)) {
          updateWallConnections(selection.id, "middleV", [selection.id], store);
        }
      });
    }

    if (translatedObjectIds.size > 0) {
      const wallsToRefresh = new Set([...selectedWallIds, ...affectedWallIds]);
      wallsToRefresh.forEach((wallId) => updateWallClippings(wallId));
      const floorSyncObjectIds = store.collectRoomBoundaryObjectIds(translatedObjectIds);
      store.syncAreaFillsForObjects(floorSyncObjectIds);
      floorSyncObjectIds.forEach((objectId) => store.markObjectForRoomUpdate(objectId));
      store.updateAffectedRooms();
    }

    store.pushToUndoStack();
    invalidate();
  };

  @action
  setView3D = (view3D: boolean) => {
    // Do not leave 3D / close the render modal while AI generation is running.
    if (this.render3DViewGenerating && !view3D) {
      return;
    }
    if (view3D && !this.view3D && this.camera) {
      this.remember2DCameraView(this.camera);
    }
    this.view3D = view3D;
    if (!view3D) {
      this.render3DViewModalVisible = false;
      this.showMeasures = true;
      this.ruler3DConstructionMode = false;
    }
    if (view3D) {
      this.view3DPreset = "birdsEye";
      this.showMeasures = false;
      this.wallConstructionMode = false;
      this.lineConstructionMode = false;
      this.rulerConstructionMode = false;
      this.areaConstructionMode = false;
      this.areaFillConstructionMode = false;
      this.areaBleedFillMode = false;
    }
    if (isLockedByOtherCollaborator()) {
      this.panning = !view3D;
    }
  };

  @action
  setHide3DMaterialTextures = (hide: boolean) => {
    this.hide3DMaterialTextures = hide;
  };

  @action
  setHide3DShadows = (hide: boolean) => {
    this.hide3DShadows = hide;
  };

  @action
  setShowCeilings = (show: boolean) => {
    this.showCeilings = show;
  };

  @action
  setView3DPreset = (
    preset: FloorPlan3DPreset,
    options: { animate?: boolean } = {},
  ) => {
    this.view3DPreset = preset;
    if (options.animate !== false) {
      this.view3DPresetRequestId += 1;
    }
  };

  openFloorplanAIRenderPanel = () => {
    this.render3DViewModalVisible = true;
  };

  closeFloorplanAIRenderPanel = () => {
    if (this.render3DViewGenerating) return;
    this.render3DViewModalVisible = false;
  };

  openRender3DViewModal = () => {
    this.render3DViewModalVisible = true;
  };

  @action
  openRoomPicker = () => {
    this.roomPickerVisible = true;
  };

  @action
  closeRoomPicker = () => {
    this.roomPickerVisible = false;
  };

  setRender3DViewGenerating = (generating: boolean) => {
    this.render3DViewGenerating = generating;
  };

  closeRender3DViewModal = () => {
    // Keep the modal open while generation is in flight so users cannot
    // exit render mode via toolbar, view tabs, or click-outside.
    if (this.render3DViewGenerating) return;
    this.render3DViewModalVisible = false;
  };

  setShowGrid = (showGrid: boolean) => {
    this.showGrid = showGrid;
  };

  setShowBlackAndWhite = (showBlackAndWhite: boolean) => {
    this.showBlackAndWhite = showBlackAndWhite;
  };

  setMinimized = (minimized: boolean) => {
    this.minimized = minimized;
  };

  setPropertyPanelMinimized = (propertyPanelMinimized: boolean) => {
    this.propertyPanelMinimized = propertyPanelMinimized;
  };

  // to delete the selection with Backspace
  delete = () => {
    if (isLockedByOtherCollaborator()) return;
    this.selections.forEach((selection) => {
      const selectable = selection as FloorplannerSelectionType;
      if (selectable.type === "wall") {
        this.floorplannerStore.removeWall(selectable.id);
      } else if (isSymbolType(selectable)) {
        this.floorplannerStore.removeSymbol(selectable.id);
      } else if (isSingleLineType(selectable)) {
        this.floorplannerStore.removeSingleLine(selectable.id);
      } else if (isRulerLineType(selectable)) {
        this.floorplannerStore.removeRulerLine(selectable.id);
      } else if (isRuler3DType(selectable)) {
        this.floorplannerStore.removeRuler3D(selectable.id);
      }
    });
    this.clearSelections();
  };

  updateGroupRef = (id: string, groupRef: THREE.Group | null) => {
    this.groupRefsMap.set(id, groupRef);
  }

  setWallDragging = (dragging: string | null) => {
    this.wallDragging = dragging;
  }

  setLineDragging = (dragging: string | null) => {
    this.lineDragging = dragging;
  }

  setRulerDragging = (dragging: string | null) => {
    this.rulerDragging = dragging;
  }

  setSnappingTolerance = (tolerance: number) => {
    this.snappingTolerance = tolerance;
  }

  setWhichEndToUpdate = (end: "start" | "end" | undefined) => {
    this.whichEndToUpdate = end;
  }

  openAddToPresentationPopup = () => {
    this.addToPresentationVisible = true;
  }
  closeAddToPresentationPopup = () => {
    this.addToPresentationVisible = false;
  }

  //Download project
  openDownloadProjectPopup = () => {
    this.downloadProjectVisible = true;
  };
  closeDownloadProjectPopup = () => {
    this.downloadProjectVisible = false;
  };

  private getSymbolDimensions(symbol: SymbolType): { width: number, height: number } {
    if (isWindowType(symbol)) {
      // windowLength is the width (along the wall), windowWidth is the height (thickness)
      return { width: symbol.windowLength, height: symbol.windowWidth };
    } else if (isSlidingDoorType(symbol)) {
      // Leaf + clear passage beside it (matches wall opening / selectionBounds).
      return { width: symbol.doorWidth * 2, height: symbol.doorWidth };
    } else if (isDoorType(symbol)) {
      return { width: symbol.doorWidth, height: symbol.doorWidth };
    } else if (isDoubleDoorType(symbol)) {
      // doubleDoorWidth represents the width of one leaf
      return { width: symbol.doubleDoorWidth, height: symbol.doubleDoorWidth };
    } else if (isCircleStairsType(symbol)) {
      return { width: symbol.circleStairsWidth, height: symbol.circleStairsWidth };
    } else if (isRectStairsType(symbol)) {
      return { width: symbol.rectStairsWidth, height: symbol.rectStairsHeight };
    } else if (isTurnStairsType(symbol)) {
      const bounds = getTurnStairsPlanBounds(symbol, {
        defaultStepSize: symbol.stairStepSize ?? this.floorplannerStore.stairStepSize,
      });
      return { width: bounds.width, height: bounds.depth };
    } else if (isSquareSymbolType(symbol)) {
      return { width: symbol.squareWidth, height: symbol.squareHeight };
    } else if (isTriangleSymbolType(symbol)) {
      return { width: symbol.triangleWidth, height: symbol.triangleHeight };
    } else if (isCircleSymbolType(symbol)) {
      return { width: symbol.circleWidth, height: symbol.circleHeight };
    } else if (isPolygonSymbolType(symbol)) {
      return { width: symbol.polygonWidth, height: symbol.polygonHeight };
    } else if (isSvgType(symbol)) {
      return { width: symbol.svgLength, height: symbol.svgHeight };
    } else if (isSmartTableType(symbol)) {
      const bounds = resolveSmartTableLayout(symbol.smartTable).bounds;
      return { width: bounds.width, height: bounds.height };
    } else if (isBookShelfType(symbol)) {
      const bounds = resolveBookShelfLayout(symbol.bookShelf).bounds;
      return { width: bounds.width, height: bounds.depth };
    }
    // Default size for unknown types
    return { width: 1, height: 1 };
  }

  // Helper method to calculate bounds of clipboard items
  private calculateClipboardBounds(items: Array<any>): { min: THREE.Vector2, max: THREE.Vector2 } {
    // First calculate the raw bounds
    const bounds = {
      min: new THREE.Vector2(Infinity, Infinity),
      max: new THREE.Vector2(-Infinity, -Infinity)
    };

    // Calculate the center of all items
    const center = new THREE.Vector2(0, 0);
    let itemCount = 0;

    items.forEach(item => {
      if (isWallType(item) || isSingleLineType(item) || isRulerLineType(item)) {
        // For lines and walls, use the midpoint
        const midpoint = new THREE.Vector2(
          (item.start.x + item.end.x) / 2,
          (item.start.y + item.end.y) / 2
        );
        center.add(midpoint);
        itemCount++;

        bounds.min.x = Math.min(bounds.min.x, item.start.x, item.end.x);
        bounds.min.y = Math.min(bounds.min.y, item.start.y, item.end.y);
        bounds.max.x = Math.max(bounds.max.x, item.start.x, item.end.x);
        bounds.max.y = Math.max(bounds.max.y, item.start.y, item.end.y);
      } else if (isSymbolType(item)) {
        // For symbols, use their position
        center.add(item.position);
        itemCount++;

        const dimensions = this.getSymbolDimensions(item);
        const halfWidth = dimensions.width / 2;
        const halfHeight = dimensions.height / 2;

        // Create points relative to center (0,0)
        let points = [
          new THREE.Vector2(-halfWidth, -halfHeight),
          new THREE.Vector2(halfWidth, -halfHeight),
          new THREE.Vector2(halfWidth, halfHeight),
          new THREE.Vector2(-halfWidth, halfHeight)
        ];

        // Apply rotation around center (0,0)
        const rotation = item.rotation || 0;
        points = points.map(point => point.rotateAround(new THREE.Vector2(0, 0), rotation));

        // Translate points to item's position
        points = points.map(point => point.add(item.position));

        // Update bounds
        points.forEach(point => {
          bounds.min.x = Math.min(bounds.min.x, point.x);
          bounds.min.y = Math.min(bounds.min.y, point.y);
          bounds.max.x = Math.max(bounds.max.x, point.x);
          bounds.max.y = Math.max(bounds.max.y, point.y);
        });
      }
    });

    // Calculate the true center of all items
    if (itemCount > 0) {
      center.divideScalar(itemCount);
    }

    // Calculate dimensions relative to the center
    const width = bounds.max.x - bounds.min.x;
    const height = bounds.max.y - bounds.min.y;

    // Return bounds centered around the content's true center
    return {
      min: new THREE.Vector2(center.x - width / 2, center.y - height / 2),
      max: new THREE.Vector2(center.x + width / 2, center.y + height / 2)
    };
  }

  /**
   * Finds an open area to paste clipboard contents
   * 
   * Algorithm Overview:
   * 1. Initial Setup:
   *    - Calculates the bounding box of clipboard contents
   *    - Collects bounds of all existing objects (walls, lines, symbols)
   * 
   * 2. Search Strategy:
   *    - Starts from the visible center
   *    - Tries positions in a spiral pattern until finding an open spot
   * 
   * @param clipboardBounds The bounds of the clipboard contents to be pasted
   * @returns The position where the clipboard contents should be pasted
   */
  /**
   * Public wrapper used by image-import insert: find a free center for content
   * of the given size, searching outward from `preferredCenter`.
   */
  findOpenContentCenter = (
    contentBounds: { min: THREE.Vector2; max: THREE.Vector2 },
    preferredCenter: THREE.Vector2,
  ): THREE.Vector2 => this.findOpenArea(contentBounds, preferredCenter);

  private findOpenArea(
    clipboardBounds: { min: THREE.Vector2, max: THREE.Vector2 },
    searchOriginOverride?: THREE.Vector2,
  ): THREE.Vector2 {
    // Get the visible bounds
    const visibleBounds = this.getVisibleBounds();
    if (!visibleBounds) {
      return searchOriginOverride?.clone() ?? new THREE.Vector2(0, 0);
    }

    // Calculate clipboard dimensions
    const clipboardWidth = clipboardBounds.max.x - clipboardBounds.min.x;
    const clipboardHeight = clipboardBounds.max.y - clipboardBounds.min.y;

    // Get all existing objects' bounds
    const existingBounds: { min: THREE.Vector2, max: THREE.Vector2 }[] = [];

    // Collect bounds from all objects
    this.floorplannerStore.wallsMap.forEach(wall => {
      const bounds = this.calculateWallBounds(wall);
      existingBounds.push(bounds);
    });

    this.floorplannerStore.symbolsMap.forEach(symbol => {
      const bounds = this.calculateSymbolBounds(symbol);
      existingBounds.push(bounds);
    });

    this.floorplannerStore.singleLinesMap.forEach(line => {
      const bounds = {
        min: new THREE.Vector2(Math.min(line.start.x, line.end.x), Math.min(line.start.y, line.end.y)),
        max: new THREE.Vector2(Math.max(line.start.x, line.end.x), Math.max(line.start.y, line.end.y))
      };
      existingBounds.push(bounds);
    });

    this.floorplannerStore.rulerLinesMap.forEach(line => {
      const bounds = {
        min: new THREE.Vector2(Math.min(line.start.x, line.end.x), Math.min(line.start.y, line.end.y)),
        max: new THREE.Vector2(Math.max(line.start.x, line.end.x), Math.max(line.start.y, line.end.y))
      };
      existingBounds.push(bounds);
    });

    // Draw the view bounds last and make it more visible
    if (this.debugPasteVisualization) {
      const renderStore = getRenderStore();
      if (renderStore?.scene) {
        // Visualize the current view bounds with margins
        const viewBoundsLine = this.createDebugLine(
          [
            visibleBounds.xMin, visibleBounds.yMin, 0,
            visibleBounds.xMax, visibleBounds.yMin, 0,
            visibleBounds.xMax, visibleBounds.yMax, 0,
            visibleBounds.xMin, visibleBounds.yMax, 0,
            visibleBounds.xMin, visibleBounds.yMin, 0
          ],
          0x000080, // Dark blue color
          1.0, // Full opacity
          3 // Thicker line
        );
        viewBoundsLine.userData.isDebugLine = true;
        renderStore.scene.add(viewBoundsLine);
      }
    }

    // Function to check if a position is valid
    const isValidPosition = (pos: THREE.Vector2): boolean => {
      const testBounds = {
        min: new THREE.Vector2(
          pos.x - clipboardWidth / 2,
          pos.y - clipboardHeight / 2
        ),
        max: new THREE.Vector2(
          pos.x + clipboardWidth / 2,
          pos.y + clipboardHeight / 2
        )
      };

      // Check for overlaps with existing objects
      for (const bound of existingBounds) {
        const gap = 0.2; // Gap between objects
        if (!(
          testBounds.max.x + gap <= bound.min.x ||
          testBounds.min.x - gap >= bound.max.x ||
          testBounds.max.y + gap <= bound.min.y ||
          testBounds.min.y - gap >= bound.max.y
        )) {
          return false;
        }
      }

      return true;
    };

    const searchOrigin =
      searchOriginOverride?.clone() ?? this.getPasteSearchOrigin(clipboardBounds);

    // First try the preferred paste center.
    if (isValidPosition(searchOrigin)) {
      this.lastPastePosition = searchOrigin.clone();

      this.pan2DCameraToBoundsIfOutsideView({
        xMin: searchOrigin.x - clipboardWidth / 2,
        xMax: searchOrigin.x + clipboardWidth / 2,
        yMin: searchOrigin.y - clipboardHeight / 2,
        yMax: searchOrigin.y + clipboardHeight / 2
      });

      return searchOrigin;
    }

    // Search in expanding squares around the preferred paste center.
    const maxAttempts = 1000;
    let attempts = 0;
    let radius = Math.max(clipboardWidth, clipboardHeight) * 0.5; // Start with half the clipboard size

    while (attempts < maxAttempts) {
      // Try positions in a square pattern at current radius
      const positions = [
        // Top edge
        ...Array.from({ length: 8 }, (_, i) => new THREE.Vector2(
          searchOrigin.x - radius + (radius * 2 * i / 7),
          searchOrigin.y + radius
        )),
        // Right edge
        ...Array.from({ length: 8 }, (_, i) => new THREE.Vector2(
          searchOrigin.x + radius,
          searchOrigin.y + radius - (radius * 2 * i / 7)
        )),
        // Bottom edge
        ...Array.from({ length: 8 }, (_, i) => new THREE.Vector2(
          searchOrigin.x + radius - (radius * 2 * i / 7),
          searchOrigin.y - radius
        )),
        // Left edge
        ...Array.from({ length: 8 }, (_, i) => new THREE.Vector2(
          searchOrigin.x - radius,
          searchOrigin.y - radius + (radius * 2 * i / 7)
        ))
      ];

      // Check all positions in current square
      for (const pos of positions) {
        if (isValidPosition(pos)) {
          this.lastPastePosition = pos.clone();

          this.pan2DCameraToBoundsIfOutsideView({
            xMin: pos.x - clipboardWidth / 2,
            xMax: pos.x + clipboardWidth / 2,
            yMin: pos.y - clipboardHeight / 2,
            yMax: pos.y + clipboardHeight / 2
          });

          return pos;
        }
        attempts++;
        if (attempts >= maxAttempts) break;
      }

      // Increase radius for next iteration
      radius += Math.max(clipboardWidth, clipboardHeight) * 0.5;
    }

    // If no position found after max attempts, return position slightly offset from the preferred paste center.
    const fallbackPos = new THREE.Vector2(
      searchOrigin.x + clipboardWidth * 0.2,
      searchOrigin.y + clipboardHeight * 0.2
    );
    this.lastPastePosition = fallbackPos.clone();

    this.pan2DCameraToBoundsIfOutsideView({
      xMin: fallbackPos.x - clipboardWidth / 2,
      xMax: fallbackPos.x + clipboardWidth / 2,
      yMin: fallbackPos.y - clipboardHeight / 2,
      yMax: fallbackPos.y + clipboardHeight / 2
    });

    return fallbackPos;
  }

  private visualizeDebugBounds(bounds: { min: THREE.Vector2, max: THREE.Vector2 }, color: number) {
    if (!this.debugPasteVisualization) return;
    const renderStore = getRenderStore();
    if (!renderStore?.scene) return;

    const points = [
      bounds.min.x, bounds.min.y, 0,
      bounds.max.x, bounds.min.y, 0,
      bounds.max.x, bounds.max.y, 0,
      bounds.min.x, bounds.max.y, 0,
      bounds.min.x, bounds.min.y, 0
    ];
    const line = this.createDebugLine(points, color);
    line.userData.isDebugLine = true;
    renderStore.scene.add(line);
  }

  private createDebugLine(points: number[], color: number, opacity: number = 0.8, linewidth: number = 2): THREE.Line {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(points), 3));
    const material = new THREE.LineBasicMaterial({
      color: color,
      transparent: true,
      opacity: opacity,
      depthTest: false,
      linewidth: linewidth
    });
    return new THREE.Line(geometry, material);
  }

  // Helper to calculate exact bounds for a wall
  private calculateWallBounds(wall: WallType): { min: THREE.Vector2, max: THREE.Vector2 } {
    // Get the exact wall width without any margin
    const wallWidth = wall.wallWidth || this.floorplannerStore.wallWidth;
    const halfWidth = wallWidth / 2;

    // Calculate wall direction and perpendicular vector
    const wallDirection = new THREE.Vector2()
      .subVectors(wall.end, wall.start)
      .normalize();
    const perpendicular = new THREE.Vector2(-wallDirection.y, wallDirection.x)
      .multiplyScalar(halfWidth);

    // Calculate the four corners of the wall
    const corners = [
      new THREE.Vector2().addVectors(wall.start, perpendicular),
      new THREE.Vector2().subVectors(wall.start, perpendicular),
      new THREE.Vector2().addVectors(wall.end, perpendicular),
      new THREE.Vector2().subVectors(wall.end, perpendicular)
    ];

    // Find the min/max coordinates
    return {
      min: new THREE.Vector2(
        Math.min(...corners.map(c => c.x)),
        Math.min(...corners.map(c => c.y))
      ),
      max: new THREE.Vector2(
        Math.max(...corners.map(c => c.x)),
        Math.max(...corners.map(c => c.y))
      )
    };
  }

  // Helper to calculate exact bounds for a symbol
  private calculateSymbolBounds(symbol: SymbolType): { min: THREE.Vector2, max: THREE.Vector2 } {
    const dimensions = this.getSymbolDimensions(symbol);
    let points: THREE.Vector2[] = [];

    if (isDoorType(symbol) || isSlidingDoorType(symbol) || isDoubleDoorType(symbol)) {
      const width = dimensions.width;
      const height = dimensions.height;
      const openAngle = symbol.openAngle || Math.PI / 2;
      const flipY = symbol.flipVertical ? -1 : 1;

      // Sliding doors report the full opening; double doors report one leaf (swing
      // arcs expand the AABB). Hinged doors report leaf width and use half of it here.
      const doorWidth = isDoubleDoorType(symbol) || isSlidingDoorType(symbol) ? width : width / 2;
      points = [
        new THREE.Vector2(0, 0),
        new THREE.Vector2(doorWidth, 0),
        new THREE.Vector2(doorWidth, height),
        new THREE.Vector2(0, height)
      ];

      // For double doors, add swing arc points
      if (isDoubleDoorType(symbol)) {
        const segments = 16;
        // Left door swing
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * openAngle;
          points.push(new THREE.Vector2(
            doorWidth / 2 * Math.cos(angle),
            height * Math.sin(angle) * flipY
          ));
        }
        // Right door swing
        for (let i = 0; i <= segments; i++) {
          const angle = (i / segments) * openAngle;
          points.push(new THREE.Vector2(
            doorWidth * 1.5 + doorWidth / 2 * Math.cos(-angle),
            height * Math.sin(angle) * flipY
          ));
        }
      }

      // Apply rotation around bottom-left corner (0,0)
      const rotation = symbol.rotation || 0;
      points = points.map(point => point.rotateAround(new THREE.Vector2(0, 0), rotation));

      // Finally translate all points to the symbol's position
      points = points.map(point => point.add(symbol.position));
    } else if (isWindowType(symbol)) {
      // For windows, calculate points relative to center
      const halfWidth = dimensions.width / 2;
      const halfHeight = dimensions.height / 2;

      // Create points relative to center (0,0)
      points = [
        new THREE.Vector2(-halfWidth, -halfHeight),
        new THREE.Vector2(halfWidth, -halfHeight),
        new THREE.Vector2(halfWidth, halfHeight),
        new THREE.Vector2(-halfWidth, halfHeight)
      ];

      // Apply rotation around center (0,0)
      const rotation = symbol.rotation || 0;
      points = points.map(point => point.rotateAround(new THREE.Vector2(0, 0), rotation));

      // Finally translate all points to the symbol's position
      points = points.map(point => point.add(symbol.position));
    } else {
      // For other symbols, calculate points relative to center
      const halfWidth = dimensions.width / 2;
      const halfHeight = dimensions.height / 2;

      // Create points relative to origin (0,0)
      points = [
        new THREE.Vector2(-halfWidth, -halfHeight),
        new THREE.Vector2(halfWidth, -halfHeight),
        new THREE.Vector2(halfWidth, halfHeight),
        new THREE.Vector2(-halfWidth, halfHeight)
      ];

      // Apply rotation around origin (0,0)
      const rotation = symbol.rotation || 0;
      points = points.map(point => point.rotateAround(new THREE.Vector2(0, 0), rotation));

      // Finally translate all points to the symbol's position
      points = points.map(point => point.add(symbol.position));
    }

    // Calculate bounds from points
    return {
      min: new THREE.Vector2(
        Math.min(...points.map(p => p.x)),
        Math.min(...points.map(p => p.y))
      ),
      max: new THREE.Vector2(
        Math.max(...points.map(p => p.x)),
        Math.max(...points.map(p => p.y))
      )
    };
  }

  private doLinesIntersect(start1: THREE.Vector2, end1: THREE.Vector2, start2: THREE.Vector2, end2: THREE.Vector2, gap: number): boolean {
    // First do a quick bounding box check
    const bounds1 = {
      min: new THREE.Vector2(Math.min(start1.x, end1.x) - gap, Math.min(start1.y, end1.y) - gap),
      max: new THREE.Vector2(Math.max(start1.x, end1.x) + gap, Math.max(start1.y, end1.y) + gap)
    };

    const bounds2 = {
      min: new THREE.Vector2(Math.min(start2.x, end2.x) - gap, Math.min(start2.y, end2.y) - gap),
      max: new THREE.Vector2(Math.max(start2.x, end2.x) + gap, Math.max(start2.y, end2.y) + gap)
    };

    // If bounding boxes don't overlap, lines can't intersect
    if (bounds1.max.x < bounds2.min.x || bounds1.min.x > bounds2.max.x ||
      bounds1.max.y < bounds2.min.y || bounds1.min.y > bounds2.max.y) {
      return false;
    }

    // Calculate line vectors
    const v1 = end1.clone().sub(start1);
    const v2 = end2.clone().sub(start2);
    const v3 = start2.clone().sub(start1);

    // Calculate cross products
    const cross1 = v1.x * v3.y - v1.y * v3.x;
    const cross2 = v1.x * v2.y - v1.y * v2.x;

    if (Math.abs(cross2) < 1e-10) {
      // Lines are parallel, check overlap
      const dot = v1.dot(v2);
      const len1Squared = v1.lengthSq();
      const t0 = v1.dot(v3) / len1Squared;
      const t1 = t0 + (dot / len1Squared);

      return Math.max(0, Math.min(1, t0), Math.min(1, t1)) <=
        Math.min(1, Math.max(0, t0), Math.max(0, t1));
    }

    // Calculate intersection parameters
    const t = cross1 / cross2;
    const s = (v2.x * v3.y - v2.y * v3.x) / cross2;

    // Check if intersection point lies within both line segments
    return (t >= -gap && t <= 1 + gap && s >= -gap && s <= 1 + gap);
  }

  // Function to check if a position is valid
  private isValidPosition(pos: THREE.Vector2, clipboardBounds: { min: THREE.Vector2, max: THREE.Vector2 }, existingBounds: Array<{ min: THREE.Vector2, max: THREE.Vector2 }>): boolean {
    const clipboardWidth = clipboardBounds.max.x - clipboardBounds.min.x;
    const clipboardHeight = clipboardBounds.max.y - clipboardBounds.min.y;

    // Calculate test bounds relative to the paste position (which is the top-left corner)
    const testBounds = {
      min: new THREE.Vector2(pos.x, pos.y),
      max: new THREE.Vector2(pos.x + clipboardWidth, pos.y + clipboardHeight)
    };

    // Debug visualization
    if (this.debugPasteVisualization) {
      this.visualizeDebugBounds(testBounds, 0x444444); // Dark grey for test position
    }

    // Calculate offset for the pasted items
    const offset = pos.clone().sub(clipboardBounds.min);

    // First check clipboard walls/lines against existing walls/lines
    const pastedWallsAndLines = this.clipboard.filter(item =>
      isWallType(item) || isSingleLineType(item) || isRulerLineType(item)
    );

    // Check against all existing walls and lines
    for (const item of pastedWallsAndLines) {
      const pastedStart = new THREE.Vector2(item.start.x + offset.x, item.start.y + offset.y);
      const pastedEnd = new THREE.Vector2(item.end.x + offset.x, item.end.y + offset.y);

      // Check against walls
      for (const wall of this.floorplannerStore.wallsMap.values()) {
        const gap = (wall.wallWidth || this.floorplannerStore.wallWidth) / 2 + 0.2;
        if (this.doLinesIntersect(pastedStart, pastedEnd, wall.start, wall.end, gap)) {
          return false;
        }
      }

      // Check against single lines
      for (const line of this.floorplannerStore.singleLinesMap.values()) {
        if (this.doLinesIntersect(pastedStart, pastedEnd, line.start, line.end, 0.2)) {
          return false;
        }
      }

      // Check against ruler lines
      for (const line of this.floorplannerStore.rulerLinesMap.values()) {
        if (this.doLinesIntersect(pastedStart, pastedEnd, line.start, line.end, 0.2)) {
          return false;
        }
      }
    }

    // Then check clipboard symbols against everything using bounding boxes
    const pastedSymbols = this.clipboard.filter(item => isSymbolType(item));
    for (const symbol of pastedSymbols) {
      const symbolBounds = this.calculateSymbolBounds({
        ...symbol,
        position: new THREE.Vector2(symbol.position.x + offset.x, symbol.position.y + offset.y)
      });

      // Check against all existing bounds
      for (const bound of existingBounds) {
        const gap = 0.2;
        if (!(
          symbolBounds.max.x + gap <= bound.min.x ||
          symbolBounds.min.x - gap >= bound.max.x ||
          symbolBounds.max.y + gap <= bound.min.y ||
          symbolBounds.min.y - gap >= bound.max.y
        )) {
          return false;
        }
      }
    }

    return true;
  }

  // Add new method to handle window blur
  private handleWindowBlur = () => {
    this.setShiftPressed(false);
    this.setAltPressed(false);
    this.endSpacePanning();
  }

  // Group management methods
  groupSelected = () => {
    if (isLockedByOtherCollaborator()) return;
    if (this.selections.length < 2) return;

    const objectIds = this.selections.map(s => s.id);
    if (!this.floorplannerStore.canObjectsBeGrouped(objectIds)) {
      console.warn("Some objects are already grouped");
      return;
    }

    const groupId = this.floorplannerStore.createGroup(objectIds);
    if (groupId) {
      this.floorplannerStore.beginUndoTransaction('groupObjects');
      this.floorplannerStore.recordGroupChange(groupId, 'add');
      this.floorplannerStore.pushToUndoStack();
    }
  };

  ungroupSelected = () => {
    if (isLockedByOtherCollaborator()) return;
    if (this.selections.length === 0) return;

    this.floorplannerStore.beginUndoTransaction('ungroupObjects');

    // Get all groups that contain selected objects
    const groupIds = new Set<string>();
    this.selections.forEach(selection => {
      const groupId = this.floorplannerStore.getObjectGroup(selection.id);
      if (groupId) {
        if (!groupIds.has(groupId)) {
          // Record state before deletion
          this.floorplannerStore.recordGroupForUndo(groupId);
          groupIds.add(groupId);
        }
      }
    });

    // Ungroup all found groups
    groupIds.forEach(groupId => {
      this.floorplannerStore.ungroupObjects(groupId);
      // Record deletion
      this.floorplannerStore.recordGroupChange(groupId, 'delete');
    });

    this.floorplannerStore.pushToUndoStack();
  };

  canGroupSelected = (): boolean => {
    if (isLockedByOtherCollaborator()) return false;
    if (this.selections.length < 2) return false;

    const objectIds = this.selections.map(s => s.id);
    return this.floorplannerStore.canObjectsBeGrouped(objectIds);
  };

  canUngroupSelected = (): boolean => {
    if (isLockedByOtherCollaborator()) return false;
    if (this.selections.length === 0) return false;

    // Check if any selected object is in a group
    return this.selections.some(selection =>
      this.floorplannerStore.isObjectInGroup(selection.id)
    );
  };

  // Symbol library state management
  @action
  setSymbolLibraryActiveTab = (tab: string) => {
    this.symbolLibraryActiveTab = tab;
  };

  @action
  setSymbolLibraryScrollPosition = (tab: string, position: number) => {
    this.symbolLibraryScrollPositions.set(tab, position);
  };

  getSymbolLibraryScrollPosition = (tab: string): number => {
    return this.symbolLibraryScrollPositions.get(tab) || 0;
  };

}

export const editorStore = new EditorStore();
export const editorStoreContext = React.createContext<EditorStore>(editorStore);
export const useEditorStore = () => useContext(editorStoreContext);

export type EditorStoreType = EditorStore;

declare global {
  var __boniEditorStore: EditorStore | undefined;
}

/**
 * Cache the editorStore singleton on the global object so modules that need a
 * late-bound reference (e.g. to avoid circular imports) can resolve it safely.
 */
if (typeof globalThis !== "undefined") {
  (globalThis as typeof globalThis & { __boniEditorStore?: EditorStore }).__boniEditorStore = editorStore;
}
